import './taskpane.css';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { agentStep, getHealth, ApiError, deleteConversation, getAuthConfig, acceptPolicy, getMe, listConversations, loadConversation, renameConversation, saveConversation, } from '../api/client';
import { acquireToken, AuthError } from '../util/auth';
import { deriveTitle, firstName, historyIsResumable, interruptedTurnEntry, summarizeConversation, TITLE_MAX_CHARS, } from '../util/session';
import { appendInterruptedTurn, appendTextToToolResults, FINALIZE_INSTRUCTION, interruptedTurnNote, recordFromToolResult, wrapUpReminder, wrapUpThreshold, } from '../util/turnRecovery';
import { executeTool, getDocumentOverview, setOldCoverSheetGuard, withTrackChangesSuspended, } from '../word/tools';
import { applyTemplate, documentHasContent, formatIntoTemplate, loadRules, loadTemplateCatalog, readOriginalCover, readRulesJson, } from '../word/template';
// "Formatar" documento existente: formatação por código + revisão por seção.
import { formatDocumentToHouseStyle, planFormattingReview, } from '../word/formatDocument';
import { describeIssues } from '../word/formatPlan';
import { DEFAULT_SKELETON_MAX_TOPICS, formatSkeletonBlock, getSkeleton, hydrateSkeleton, normalizeSkeleton, setSkeleton, SKELETON_MIN_TOPICS, SKELETON_TOPIC_MAX_CHARS, } from '../word/skeleton';
import { ASK_LABELS, ASK_REPLIES, askTagText, isConfirmKind, parseAskBlock, stripAskBlock, } from '../util/clarify';
import { normalizeSpacing } from '../word/cleanup';
import { captureDocumentFile, linkDocumentToConversation, readDocumentFingerprint, readLinkedConversationId, restoreDocumentFile, } from '../word/conversationDocument';
import { isDocxSnapshot, snapshotMeta } from '../util/documentSnapshot';
import { formatProblems, validateDocument } from '../word/validator';
import { estimateContextTokens, PROMPT_BUDGET_TOKENS, promptTokensFromUsage, PRUNE_TRIGGER_TOKENS, pruneBulkyToolBlocks, pruneOlderTurnMessages, } from '../util/context';
import { dlog } from '../util/log';
import { redactToolError } from '../util/redact';
import { accumulateTierTokens, emptyTurnTokens, logLlmInteraction, logStandard, newTurnId, sessionId, } from '../util/telemetry';
import { ACCEPT_ATTR, rememberInsertableImages, formatBytes, processFile, validateBatch, } from '../util/attachments';
import { buildAttachmentContext, mergeConversationAttachments, newAttachmentsNote, stripAttachmentContext, } from '../util/attachmentContext';
// Markdown sem quebras de linha "soltas" virando <br> demais; GFM ligado.
marked.setOptions({ gfm: true, breaks: true });
/** Href sentinela das referências clicáveis (RF04): o modelo emite
 *  `[Título exato do tópico](#norven-ref)` e o clique resolve por texto. */
const REF_HREF = '#norven-ref';
/** Rodapé da 1ª fala do mIAu — o render marca o parágrafo com `.greet-note`. */
const GREET_CAVEAT = 'Observação: Sempre valide o que estou fazendo, posso cometer erros.';
/** Converte markdown -> HTML sanitizado (seguro contra XSS de prompt-injection). */
function renderMarkdown(text) {
    const rawHtml = marked.parse(text, { async: false });
    const clean = DOMPurify.sanitize(rawHtml);
    // Pós-sanitização: transforma os links de referência (#norven-ref) em botões
    // navegáveis. Feito DEPOIS do sanitize e adicionando SÓ atributos seguros
    // (class/role/tabindex/title) — não reabre superfície de XSS. Um href de
    // fragmento local (#...) já é permitido pelo DOMPurify por padrão.
    const holder = document.createElement('div');
    holder.innerHTML = clean;
    holder.querySelectorAll('p').forEach((p) => {
        if ((p.textContent || '').trim() === GREET_CAVEAT) {
            p.classList.add('greet-note');
        }
    });
    holder.querySelectorAll(`a[href="${REF_HREF}"]`).forEach((a) => {
        const label = (a.textContent || '').trim();
        a.setAttribute('role', 'button');
        a.setAttribute('tabindex', '0');
        a.setAttribute('title', `Ir para “${label}” no documento`);
    });
    return holder.innerHTML;
}
/**
 * mIAu (Copiloto Norven) — chat com agent loop real contra Bedrock.
 *
 * Fluxo:
 * 1. Usuário digita prompt + opcionalmente seleciona texto
 * 2. Enviamos pro backend (FastAPI) + contexto do documento
 * 3. Backend chama Claude via Bedrock com as ferramentas definidas
 * 4. Se Claude pedir tool_use, executamos as ferramentas aqui (Office.js)
 * 5. Devolvemos o tool_result, reenviamos para continuar o loop
 * 6. Quando Claude termina (stop_reason != "tool_use"), fim do turno
 */
let promptEl;
let sendBtn;
let messagesEl;
/** Tipo de documento escolhido no seletor de tipo e suas regras (base + tipo). A
 *  conversa fica "ligada" a este tipo: enviamos `documentType`/`selectedRules`
 *  em todo request. `null` = ainda não escolheu (chat bloqueado). */
let documentType = null;
let selectedRules = '';
/** Catálogo do seletor — a saudação lê o artigo (a/o) daqui, sem código novo por tipo. */
let templateCatalog = [];
/** Botão "Desfazer" atualmente exibido (ancorado na última resposta que
 *  alterou o documento). Só existe UM por vez: desfazer vale para a ÚLTIMA
 *  alteração, não para um histórico de desfazimentos. `null` = nenhum. */
let currentUndoEl = null;
/**
 * Troca de tela via atributo data-view no elemento .app.
 * O CSS é a única fonte da verdade — impossível mostrar as duas telas ao
 * mesmo tempo, independente de quantas vezes o JS manipule o DOM.
 */
function setView(view) {
    const appEl = document.getElementById('app');
    if (appEl)
        appEl.dataset.view = view;
}
// ======================================================================
// Validador do documento (badge no header)
// ======================================================================
// Roda ao FINAL de cada turno que escreveu no documento (e após aplicar o
// template). Determinístico, sem LLM — ver word/validator.ts. Só valida e
// informa; nunca altera o documento.
// Rótulos do badge: curtos e sem emoji — o CSS já põe o ponto de cor (verde =
// válido, laranja = desvio) e aplica maiúsculas. "Desvio" em vez de "problema":
// é o vocabulário do sistema de design (o documento desviou do padrão).
const VALIDATION_OK_TEXT = 'válido';
const validationProblemText = (n) => `${n} ${n === 1 ? 'desvio' : 'desvios'}`;
const VALIDATION_OK_TIP = 'Na última validação eu não encontrei desvios de formatação ou padronização.';
const VALIDATION_PROBLEM_TIP_PREFIX = 'Na última validação eu encontrei estes desvios de formatação e padronização:\n';
/**
 * Validador LIGADO? (`VALIDATOR_FLAG` no .env, lido em build-time.)
 *
 * Desligado, a funcionalidade não existe para o usuário: o badge sai do DOM no
 * boot e `runValidation` devolve na porta. Esconder só o botão deixaria a
 * varredura rodando a cada turno — custo de Office.js e telemetria de uma
 * feature que o usuário não vê.
 */
const VALIDATOR_ON = __VALIDATOR_ENABLED__;
/** Evita validações concorrentes (turnos rápidos em sequência). */
let validationRunning = false;
function setValidationRunningState(running) {
    const badge = document.getElementById('validation-badge');
    if (badge) {
        badge.classList.toggle('running', running);
        badge.setAttribute('aria-disabled', String(running));
        badge.setAttribute('aria-label', running ? 'Validando documento' : 'Validar documento');
    }
}
function updateValidationBadge(report) {
    const control = document.getElementById('validation-control');
    const badge = document.getElementById('validation-badge');
    const textEl = document.getElementById('validation-badge-text');
    const tipEl = document.getElementById('validation-tooltip');
    if (!badge || !textEl || !tipEl)
        return;
    if (control)
        control.hidden = false;
    badge.classList.toggle('valid', report.ok);
    badge.classList.toggle('invalid', !report.ok);
    textEl.textContent = report.ok
        ? VALIDATION_OK_TEXT
        : validationProblemText(report.violations.length);
    // Documento longo: a varredura para em MAX_PARAGRAPHS. Sem dizer isso, um
    // "válido" viraria uma afirmação sobre o documento INTEIRO que o validador
    // não verificou — em doc de 60 páginas ele viu só o começo.
    const scanNote = report.truncated
        ? `\n\nDocumento longo: verifiquei os primeiros ${report.checkedParagraphs} parágrafos. ` +
            'O que vem depois não entrou nesta checagem.'
        : '';
    tipEl.textContent =
        (report.ok ? VALIDATION_OK_TIP : VALIDATION_PROBLEM_TIP_PREFIX + formatProblems(report)) +
            scanNote;
}
async function runValidation(trigger = 'auto') {
    var _a, _b;
    if (!VALIDATOR_ON || !documentType || validationRunning)
        return;
    validationRunning = true;
    setValidationRunningState(true);
    const previousStatus = (_b = (_a = document.getElementById('status')) === null || _a === void 0 ? void 0 : _a.textContent) !== null && _b !== void 0 ? _b : '';
    if (trigger === 'manual')
        setStatus('validando');
    try {
        const report = await validateDocument(documentType);
        if (report) {
            lastValidationReport = report; // alimenta o bloco "Conformidade" do turno
            updateValidationBadge(report);
            // Telemetria: só contagens/regras — nunca o conteúdo do documento.
            logStandard({
                type: report.ok ? 'SUCCESS' : 'WARNING',
                event: 'validation.run',
                detail: {
                    trigger,
                    ok: report.ok,
                    violation_count: report.violations.length,
                    // `rule_ids` e não `rules`: o schema do canal standard PROÍBE a chave
                    // `rules` (lá ela significa o manual do rules.json, que não pode ir
                    // para log). Com o nome colidindo, todo `validation.run` era recusado
                    // com 422 e a telemetria do validador nunca chegava a ser gravada.
                    rule_ids: [...new Set(report.violations.map((v) => v.rule))].slice(0, 10),
                    checked_paragraphs: report.checkedParagraphs,
                    checked_images: report.checkedImages,
                    images_skipped: report.imagesSkipped,
                },
            });
        }
    }
    finally {
        validationRunning = false;
        setValidationRunningState(false);
        if (trigger === 'manual')
            setStatus(previousStatus);
    }
}
/** Dispara a validação em background (fire-and-forget; nunca quebra o chat). */
function scheduleValidation() {
    void runValidation('auto');
}
function wireValidationBadge() {
    var _a;
    const badge = document.getElementById('validation-badge');
    if (!VALIDATOR_ON) {
        // Removido, e não apenas escondido: `updateValidationBadge` liga o
        // `hidden` do controle, e um elemento que não existe não volta por engano.
        (_a = document.getElementById('validation-control')) === null || _a === void 0 ? void 0 : _a.remove();
        return;
    }
    if (!badge)
        return;
    const trigger = (event) => {
        event.preventDefault();
        event.stopPropagation();
        void runValidation('manual');
    };
    badge.onclick = trigger;
    badge.onkeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ')
            return;
        trigger(event);
    };
}
/** AbortController do turno em andamento (para o botão "Parar"). `null` =
 *  nada rodando. */
let currentAbort = null;
/** Como o último turno terminou — a revisão por seção decide se segue. */
let lastTurnStatus = null;
/** Prefixo da linha de status durante a revisão por seção ("seção 2 de 5"). */
let statusPrefix = '';
/**
 * Snapshot do documento (OOXML do corpo) tirado ANTES da 1ª escrita do turno
 * atual. Permite ao usuário reverter, num clique, tudo que o mIAu alterou
 * naquele turno — o Office.js não expõe um "undo" nativo, então restauramos o
 * estado anterior via insertOoxml(replace). `null` = nada para desfazer.
 */
let undoSnapshot = null;
/** Ferramentas que MODIFICAM o documento (vs. as de leitura). Usado para saber
 *  quando tirar o snapshot e quando exibir o botão "Desfazer". */
const WRITE_TOOLS = new Set([
    'insert_paragraph', 'insert_text', 'replace_text', 'replace_paragraphs',
    'fill_content_control', 'insert_table', 'insert_list', 'apply_formatting',
    'apply_paragraph_style', 'apply_paragraph_styles', 'add_comment', 'delete_text',
    'delete_paragraphs', 'move_text',
    'delete_table',
    'update_document_fields', 'resize_image', 'move_image', 'delete_image',
    // Threads de comentário (RF08/RF09) alteram o documento — entram aqui para
    // ter snapshot/Desfazer como qualquer escrita.
    'reply_comment', 'resolve_comment', 'edit_commented_text',
]);
/**
 * Captura o OOXML do corpo do documento (estado atual). Serve ao "Desfazer",
 * que volta no MESMO documento (estilos e seção já estão lá). Para gravar com a
 * conversa não serve sozinho — ver `snapshotForConversation`.
 */
async function snapshotDocument() {
    try {
        return await Word.run(async (context) => {
            const ooxml = context.document.body.getOoxml();
            await context.sync();
            return ooxml.value;
        });
    }
    catch (e) {
        console.warn('[snapshotDocument] falhou:', e);
        return null;
    }
}
// ======================================================================
// Memória da conversa (entre turnos)
// ======================================================================
/**
 * Histórico completo (formato Anthropic), acumulado entre TURNOS do chat —
 * não só dentro de um turno (que já encadeava tool_use/tool_result). Sem
 * isso, cada mensagem do usuário era tratada como uma conversa nova: o
 * Claude não lembrava do que foi dito 2 mensagens atrás. Vive só nesta aba
 * (memória do processo); fechar o painel reseta a conversa.
 *
 * Só é ATUALIZADO no caminho de SUCESSO de um turno (stop_reason !=
 * "tool_use"), que sempre termina em uma mensagem `assistant` válida. Isso
 * evita persistir um histórico que termina em `tool_use` sem o
 * `tool_result` correspondente (violaria a alternância de papéis exigida
 * pela API da Anthropic na próxima chamada) caso o turno seja abortado
 * (loop travado, limite de passos, erro de rede).
 */
let conversationHistory = [];
/**
 * Teto de mensagens guardadas, para a conversa não crescer sem limite (custo
 * de tokens) em sessões muito longas.
 *
 * Cortar é mais delicado do que parece: um único TURNO pode conter vários pares
 * user/assistant (cada `tool_use` do assistente exige um `tool_result` do user
 * no passo seguinte). Cortar no meio de um turno deixaria um `tool_use` sem
 * `tool_result` (ou vice-versa) — a API da Anthropic rejeita isso. Por isso
 * cortamos só em FRONTEIRA DE TURNO: uma mensagem `user` cujo `content` é uma
 * STRING (a mensagem montada por buildUserTurnText); os `tool_result` têm
 * `content` em array, então não são confundidos com início de turno.
 *
 * Se não houver fronteira segura que respeite o teto (ex.: um único turno
 * gigante), preferimos NÃO cortar a corromper o histórico.
 */
const MAX_HISTORY_MESSAGES = 60;
function trimHistory(history) {
    if (history.length <= MAX_HISTORY_MESSAGES)
        return history;
    for (let i = 0; i < history.length; i++) {
        const m = history[i];
        const isTurnStart = m.role === 'user' && typeof m.content === 'string';
        if (isTurnStart && history.length - i <= MAX_HISTORY_MESSAGES) {
            return history.slice(i);
        }
    }
    return history;
}
/** Separa o bloco de contexto do prompt do usuário, dentro da mensagem do
 *  turno. Usado para REMOVER o contexto dos turnos antigos ao guardar (só o
 *  turno atual carrega contexto fresco — ver stripContextBlocks). */
const USER_PROMPT_SEP = '\nPedido do usuário:\n';
/** Desfecho do turno ANTERIOR quando falhou (LOOP_GUARD/MAX_STEPS/ERROR).
 *  Vai como nota no contexto do próximo turno — o planejador lê o mesmo texto —
 *  dizendo o que fazer diferente. Consumido (e limpo) no início do turno
 *  seguinte. É a frase inteira: cada desfecho pede uma conduta diferente
 *  (limite de passos pede CONTINUAR em partes; loop pede outro caminho). */
let lastTurnFailure = null;
/** Nota para o 1º turno depois de retomar uma conversa MANTENDO o documento
 *  aberto, que mudou desde o último salvamento (edição manual, Desfazer): o
 *  histórico fala de um documento que já não é exatamente este. */
let resumeNote = null;
/** O usuário clicou "Desfazer" desde o último turno? O documento REVERTEU e o
 *  histórico da conversa ficou mentiroso (fala de edições que não existem
 *  mais) — o próximo turno precisa ser avisado. */
let documentRevertedNote = false;
/** Assinatura do documento no FIM do último turno (palavras/parágrafos).
 *  Comparada no início do turno seguinte: divergência = o documento mudou
 *  FORA do chat (edição manual ou Ctrl+Z) — o modelo não pode assumir que o
 *  estado que ele conhecia ainda vale. `null` = sem baseline ainda. */
let docStateAtTurnEnd = null;
/** Último relatório do validador (para o bloco "Conformidade" do contexto). */
let lastValidationReport = null;
// Tetos do outline no contexto (tokens): títulos suficientes para ancorar sem
// inflar o turno. 80 cobre documentos de ~60 páginas (custa pouco: 1 linha por
// título); acima disso o modelo completa com get_document_overview sob demanda.
const OUTLINE_MAX_HEADINGS = 80;
const CONFORMITY_MAX_ITEMS = 5;
/**
 * Outline compacto do documento para o contexto do turno. Elimina a "cegueira"
 * do início do turno: o modelo (e o PLANEJADOR, que lê o mesmo texto) vê os
 * títulos reais com índice — âncoras deixam de ser chute e a 1ª chamada de
 * leitura vira desnecessária na maioria dos pedidos posicionais.
 */
function formatOutlineBlock(ov) {
    var _a, _b, _c;
    if (!ov)
        return '';
    const heads = ((_a = ov.outline) !== null && _a !== void 0 ? _a : []).slice(0, OUTLINE_MAX_HEADINGS);
    const lines = heads.map((h) => {
        var _a;
        const indent = '  '.repeat(Math.max(0, ((_a = h.level) !== null && _a !== void 0 ? _a : 1) - 1));
        return `  [${h.index}] ${indent}${h.text}`;
    });
    const extras = [
        typeof ov.tableCount === 'number' ? `tabelas: ${ov.tableCount}` : '',
        ov.imageCount ? `imagens: ${ov.imageCount.inline} inline / ${ov.imageCount.floating} flutuantes` : '',
        `sumário: ${ov.hasTableOfContents ? 'sim' : 'não'}`,
    ].filter(Boolean);
    const body = lines.length ? lines.join('\n') : '  (nenhum título no corpo ainda)';
    const assunto = ov.lede ? `\nComeço do corpo (o assunto do documento): «${ov.lede}»\n` : '';
    const hidden = ((_c = (_b = ov.outline) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) - OUTLINE_MAX_HEADINGS;
    const more = hidden > 0 ? `\n  …(+${hidden} títulos; outline completo via get_document_overview)` : '';
    return `\nEstrutura do documento (títulos do corpo, [índice do parágrafo]):\n${body}${more}\n(${extras.join(' · ')})\n${assunto}`;
}
/** Bloco "Conformidade" — feedback do validador determinístico p/ o modelo. */
function formatConformityBlock(report) {
    if (!report)
        return '';
    // Documento longo com varredura parcial: o modelo precisa saber disso mesmo
    // quando NÃO há violação, senão "validador não apontou nada" vira "documento
    // inteiro conforme" — e o validador só olhou o começo.
    const scanNote = report.truncated
        ? `\nConformidade: a checagem cobriu só os primeiros ${report.checkedParagraphs} parágrafos ` +
            '(documento longo). Não afirme que o documento inteiro está conforme.\n'
        : '';
    if (report.ok || !report.violations.length)
        return scanNote;
    const items = report.violations
        .slice(0, CONFORMITY_MAX_ITEMS)
        .map((v) => `${v.rule}${v.section ? ` em «${v.section}»` : ''}`);
    const rest = report.violations.length - CONFORMITY_MAX_ITEMS;
    const more = rest > 0 ? `; +${rest}` : '';
    return (`\nConformidade (validador de padronização): ${report.violations.length} problema(s) — ` +
        `${items.join('; ')}${more}. Corrija ao mexer nas regiões afetadas.${scanNote ? '' : '\n'}` +
        scanNote);
}
/** Monta o texto da mensagem do usuário (prompt + contexto do documento),
 *  igual ao que o backend fazia só no 1º turno (_build_first_message em
 *  services/agent.py). Agora todo turno é enviado via `messages`, então essa
 *  montagem precisa acontecer aqui a cada novo pedido — inclusive contexto
 *  do documento ATUALIZADO (ele pode ter mudado desde o turno anterior).
 *  Tudo ANTES de USER_PROMPT_SEP é contexto volátil: os turnos antigos o
 *  perdem ao entrar no histórico (stripContextBlocks). */
/** O título da capa ainda é o placeholder do template? (metadata já traz os
 *  content controls com tag+text — sem custo extra de leitura). */
function coverTitleIsPlaceholder(metadata) {
    var _a, _b;
    const cc = ((_a = metadata.content_controls) !== null && _a !== void 0 ? _a : []).find((c) => c.tag === 'document_title');
    const text = ((_b = cc === null || cc === void 0 ? void 0 : cc.text) !== null && _b !== void 0 ? _b : '').trim().toLowerCase();
    return text.startsWith('título do documento') || text.startsWith('titulo do documento');
}
/** "«título» — cliente «X»" da capa preenchida; vazio sem capa ou no placeholder. */
function coverSubject(metadata) {
    const campo = (tag) => {
        var _a, _b;
        const cc = ((_a = metadata.content_controls) !== null && _a !== void 0 ? _a : []).find((c) => c.tag === tag);
        const t = ((_b = cc === null || cc === void 0 ? void 0 : cc.text) !== null && _b !== void 0 ? _b : '').replace(/\s+/g, ' ').trim();
        return /^(título do documento|titulo do documento|nome da (área ou )?empresa|nome do cliente|_*)$/i.test(t) ? '' : t;
    };
    const titulo = campo('document_title');
    const cliente = campo('client_name');
    return [titulo ? `«${titulo}»` : '', cliente ? `cliente «${cliente}»` : ''].filter(Boolean).join(' — ');
}
/**
 * Nada no documento diz do que ele trata: capa sem título nem cliente e corpo
 * sem texto, tabela ou figura. Títulos e esqueleto sozinhos não contam — pelo
 * mesmo motivo do "Começo do corpo": "Visão Geral", "Escopo" não dizem o
 * assunto. Aí não há assunto para um pedido de escrita contrariar — é ele que
 * o define. Medido: sem esta linha, o planejador julgava "sem relação com o
 * documento" o 1º pedido de um documento novo (inclusive com o esqueleto
 * padrão confirmado), e o executor recusava. Sem overview, o painel não sabe,
 * e não afirma nada.
 */
function documentHasNoSubject(metadata, ov) {
    var _a, _b;
    if (!ov)
        return false;
    return (!coverSubject(metadata) &&
        !ov.lede &&
        !ov.tableCount &&
        !((_a = ov.imageCount) === null || _a === void 0 ? void 0 : _a.inline) &&
        !((_b = ov.imageCount) === null || _b === void 0 ? void 0 : _b.floating));
}
function buildUserTurnText(prompt, selection, metadata, overview) {
    var _a;
    let context = 'Contexto do documento:\n';
    if (metadata.filename)
        context += `- Arquivo: ${metadata.filename}\n`;
    context += `- Palavras: ${metadata.word_count}\n`;
    context += `- Parágrafos: ${metadata.paragraph_count}\n`;
    if ((_a = metadata.content_controls) === null || _a === void 0 ? void 0 : _a.length) {
        context += `- Marcações: ${metadata.content_controls.length}\n`;
    }
    // Título da capa ainda no placeholder: sinaliza para o modelo criar um
    // título TEMÁTICO ao preencher conteúdo (regra correspondente no prompt).
    if (coverTitleIsPlaceholder(metadata)) {
        context += '- Capa: o título ainda é o placeholder "Título do documento".\n';
    }
    else {
        // Título e cliente da capa dizem do que o documento trata — é por eles que
        // se julga se um conteúdo pedido tem relação com ele (ver "Fora de escopo").
        const capa = coverSubject(metadata);
        if (capa)
            context += `- Capa: ${capa}\n`;
    }
    // Mesmo texto que o backend procura (SUBJECTLESS_CONTEXT_MARKER, em bedrock_client.py).
    if (documentHasNoSubject(metadata, overview)) {
        context += '- Assunto: nenhum ainda — sem capa preenchida nem conteúdo no corpo (títulos e esqueleto sozinhos não dizem do que o documento trata).\n';
    }
    context += formatOutlineBlock(overview);
    // Plano de tópicos definido pelo usuário. Entra DEPOIS do outline de
    // propósito: o outline é o estado real do documento, o esqueleto é a
    // intenção — e o bloco diz isso explicitamente para o modelo não confundir
    // "planejado" com "já escrito".
    context += formatSkeletonBlock();
    context += formatConformityBlock(lastValidationReport);
    if (lastTurnFailure) {
        context += `\nNota sobre o pedido ANTERIOR deste chat: ${lastTurnFailure}\n`;
    }
    // Ciência do estado REAL: se o usuário desfez (botão) ou o documento mudou
    // fora do chat (Ctrl+Z, edição manual) desde o fim do último turno, o
    // histórico da conversa ficou defasado — avisa para o modelo reler em vez
    // de assumir que as edições passadas ainda existem.
    if (documentRevertedNote) {
        context +=
            '\n⚠️ O usuário DESFEZ as alterações do turno anterior — o documento voltou ao estado ' +
                'de antes. Não assuma que suas edições anteriores existem; o outline acima é o estado atual.\n';
    }
    else if (documentDrifted(metadata)) {
        context +=
            '\n⚠️ O documento foi MODIFICADO fora do chat desde o último turno (edição manual ou ' +
                'Ctrl+Z). O outline acima reflete o estado atual; releia antes de editar regiões que você conhecia.\n';
    }
    else if (resumeNote) {
        context += `\n⚠️ ${resumeNote}\n`;
    }
    if (selection && selection.text) {
        context += `\nSeleção atual:\n${selection.text.slice(0, 2000)}\n`;
    }
    // Menções @ do usuário resolvidas em âncoras precisas (ver captureMentionContext).
    if (activeMentionLines.length) {
        context += `\nMenções do usuário (âncoras precisas indicadas com @):\n${activeMentionLines.join('\n')}\n`;
    }
    return `${context}${USER_PROMPT_SEP}${prompt}`;
}
/** O documento mudou FORA do chat desde o fim do último turno (edição manual,
 *  Ctrl+Z)? Heurística barata — palavras e parágrafos —; sem baseline, não. */
function documentDrifted(metadata) {
    return (docStateAtTurnEnd !== null &&
        (docStateAtTurnEnd.words !== metadata.word_count ||
            docStateAtTurnEnd.paragraphs !== metadata.paragraph_count));
}
/**
 * Remove o bloco "Contexto do documento" das mensagens de turno ao GUARDAR no
 * histórico, deixando só o prompt do usuário. O contexto (contagens, seleção)
 * é volátil e específico daquele momento; mantê-lo em todos os turnos antigos
 * inflaria tokens e poderia confundir o modelo com estado desatualizado. O
 * contexto FRESCO entra só na mensagem do turno atual (buildUserTurnText), em
 * voo. Mensagens não-string (tool_result) e do assistente passam intactas.
 */
function stripContextBlocks(messages) {
    return messages.map((m) => {
        if (m.role === 'user' && typeof m.content === 'string') {
            const idx = m.content.indexOf(USER_PROMPT_SEP);
            if (idx >= 0)
                return Object.assign(Object.assign({}, m), { content: m.content.slice(idx + USER_PROMPT_SEP.length) });
        }
        return m;
    });
}
/**
 * Tela de falha de inicialização. Existe porque o pior resultado possível do
 * boot é o painel EM BRANCO: `#app` nasce `hidden` no HTML e só é revelado no
 * `setupAddIn`, então qualquer erro antes disso deixava a lateral do Word vazia,
 * sem uma linha de texto que dissesse o que houve.
 */
function showBootError(detail) {
    const host = document.getElementById('boot-error');
    const msg = document.getElementById('boot-error-detail');
    if (!host || !msg)
        return;
    msg.textContent = detail;
    host.hidden = false;
    document.getElementById('app').hidden = true;
    document.getElementById('unsupported').hidden = true;
    document.getElementById('auth-gate').hidden = true;
}
// Quanto esperar o office.js antes de desistir e mostrar o erro. O carregamento
// normal leva menos de 1s; 20s cobre rede lenta sem prender o usuário numa tela
// branca para sempre.
const OFFICE_BOOT_TIMEOUT_MS = 20000;
const OFFICE_BOOT_POLL_MS = 50;
// Aguarda Office.js carregar antes de executar. O prazo é medido em tempo REAL
// (Date.now), não contando iterações: cada `setTimeout(50)` custa mais que 50ms
// e o erro acumulado esticava o limite em vários segundos.
function initializeAddin(deadline = Date.now() + OFFICE_BOOT_TIMEOUT_MS) {
    dlog('[initializeAddin] verificando se Office.js está disponível...');
    const OfficeAPI = window.Office;
    if (typeof OfficeAPI === 'undefined') {
        // Antes este retry era infinito e silencioso: com o office.js indisponível
        // (rede corporativa bloqueando o CDN, máquina offline) o painel ficava em
        // branco indefinidamente. Agora desiste e explica.
        if (Date.now() >= deadline) {
            console.error('[initializeAddin] office.js não carregou em', OFFICE_BOOT_TIMEOUT_MS, 'ms');
            showBootError('Não consegui carregar a biblioteca do Office (office.js). ' +
                'Verifique a conexão de rede e recarregue o painel.');
            return;
        }
        dlog('[initializeAddin] Office.js ainda não carregou, tentando novamente...');
        setTimeout(() => initializeAddin(deadline), OFFICE_BOOT_POLL_MS);
        return;
    }
    dlog('[initializeAddin] Office.js carregou, iniciando add-in...');
    // `setupAddIn` é async: sem este catch, qualquer falha dentro dele virava uma
    // unhandled rejection — no dev server isso abre o overlay de erro em cima do
    // painel ("Uncaught Error") e, em produção, some sem deixar rastro na tela.
    OfficeAPI.onReady((info) => {
        Promise.resolve()
            .then(() => setupAddIn(info))
            .catch((e) => {
            console.error('[setupAddIn] falha na inicialização:', e);
            showBootError('Não consegui iniciar o painel. Recarregue e tente de novo.');
        });
    });
}
async function setupAddIn(info) {
    dlog('[setupAddIn] inicializando add-in', { host: info === null || info === void 0 ? void 0 : info.host });
    const OfficeAPI = window.Office;
    if (info.host !== OfficeAPI.HostType.Word) {
        dlog('[setupAddIn] host não é Word, mostrando mensagem unsupported');
        document.getElementById('unsupported').hidden = false;
        return;
    }
    dlog('[setupAddIn] host é Word');
    // Busca elementos
    dlog('[Office.onReady] buscando elementos DOM...');
    promptEl = document.getElementById('prompt');
    sendBtn = document.getElementById('send');
    messagesEl = document.getElementById('messages');
    if (!promptEl || !sendBtn || !messagesEl) {
        console.error('[setupAddIn] ❌ ERRO: Elementos não encontrados!');
        return;
    }
    dlog('[setupAddIn] chamando wireComposer()...');
    wireComposer();
    wireAutosaveFlush();
    wireAskPanel();
    wireValidationBadge();
    wireFeedbackButton();
    wireThemeToggle();
    wireConversationTitle();
    // Ordem: backend de pé -> conta corporativa -> tela inicial. Sem o login não há
    // painel, então `bootSession` é quem revela o `#app`.
    try {
        dlog('[setupAddIn] verificando saúde do backend...');
        await getHealth();
        dlog('[setupAddIn] backend respondeu com sucesso');
        await bootSession();
    }
    catch (e) {
        console.error('[setupAddIn] erro ao conectar ao backend:', e);
        // Best-effort: se o backend está fora, este POST também falha — o emissor
        // engole o erro (fire-and-forget) e a UI segue no fluxo de erro normal.
        logStandard({
            type: 'ERROR',
            event: 'backend.unreachable',
            message: redactToolError(String(e)),
        });
        showBackendError();
    }
}
/**
 * Seletor de tipo (depois da tela inicial): lê o catálogo de tipos
 * (templates/index.json) e renderiza os cards.
 * A escolha é obrigatória e NÃO usa o modelo. O chat só aparece após selecionar.
 */
async function showTemplatePicker() {
    setView('picking');
    const listEl = document.getElementById('template-list');
    listEl.innerHTML = '';
    let catalog = [];
    try {
        catalog = await loadTemplateCatalog();
        templateCatalog = catalog;
    }
    catch (e) {
        console.error('[showTemplatePicker] falha ao carregar catálogo:', e);
        listEl.innerHTML =
            '<p class="picker-sub">Não consegui carregar os tipos de documento.</p>';
        return;
    }
    for (const t of catalog) {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'picker-card';
        const name = document.createElement('span');
        name.className = 'card-name';
        name.textContent = t.name;
        card.appendChild(name);
        if (t.description) {
            const desc = document.createElement('span');
            desc.className = 'card-desc';
            desc.textContent = t.description;
            card.appendChild(desc);
        }
        card.onclick = () => selectTemplate(t, listEl);
        listEl.appendChild(card);
    }
}
/**
 * Ao escolher um tipo: aplica o template.docx no documento, carrega as regras
 * (base + tipo) e abre o chat, deixando a conversa ligada a esse tipo.
 */
async function selectTemplate(t, listEl) {
    const subEl = document.querySelector('#template-picker .picker-sub');
    const setSub = (msg) => { if (subEl)
        subEl.textContent = msg; };
    const enableCards = () => listEl.querySelectorAll('button').forEach((b) => (b.disabled = false));
    // Evita cliques duplos enquanto processa a escolha.
    listEl.querySelectorAll('button').forEach((b) => (b.disabled = true));
    setSub(`Preparando ${t.name}…`);
    try {
        selectedRules = await loadRules(t.id, t.name);
        // Documento já tem conteúdo? Pergunta antes de aplicar o template por cima.
        // Exceção: "Outro" deve preservar documentos existentes exatamente como estão.
        let formatExisting = false;
        let formatted = null;
        const hasExistingContent = await documentHasContent();
        if (hasExistingContent && t.id === 'outro') {
            setSub(`Preparando ${t.name}…`);
        }
        else if (hasExistingContent) {
            const action = await askContentAction(); // 'clear' | 'format' | 'cancel'
            if (action === 'cancel') {
                enableCards();
                setSub('Vamos começar escolhendo o tipo de documento que você quer escrever ou reformatar.');
                return;
            }
            if (action === 'clear') {
                setSub(`Aplicando ${t.name}…`);
                await applyTemplate(t.id, 'replace'); // apaga tudo e insere o template
            }
            else {
                // "Formatar": o documento fica, o template entra na frente dele e o
                // conteúdo vai para o padrão por código (ver formatExistingDocument).
                formatted = await formatExistingDocument(t, setSub);
                formatExisting = true;
            }
        }
        else {
            // Documento vazio também usa 'replace', não 'start'. Com 'start' o Word
            // PRESERVA o parágrafo final do documento destino e, como o template tem
            // seção própria (titlePg + margens), insere uma QUEBRA DE SEÇÃO na junção
            // — o que abria página(s) em branco depois da capa e fazia o conteúdo
            // começar páginas adiante (bug relatado). 'replace' troca o
            // corpo inteiro pelo do template: uma seção só, e o corpo começa no
            // parágrafo que o próprio template reserva. Aqui não há o que perder:
            // `documentHasContent()` já garantiu que não há texto, tabela, imagem,
            // content control nem shape.
            setSub(`Aplicando ${t.name}…`);
            await applyTemplate(t.id, 'replace');
        }
        documentType = t.id;
        // Plano de tópicos é metadado do DOCUMENTO: se o arquivo já traz um, o
        // painel herda — para qualquer tipo, inclusive os sem tela de esqueleto.
        const existing = await hydrateSkeleton();
        // Tela do esqueleto: só para tipo que declara `default_skeleton` e só
        // quando o documento está partindo do template limpo. No fluxo "Formatar" a
        // estrutura vem do conteúdo extraído, e pedir um plano ali competiria com o
        // turno automático que recria o texto.
        if (!formatExisting) {
            const suggested = await loadSuggestedSkeleton(t.id);
            if (suggested !== null) {
                const confirmed = await askSkeleton(t.name, existing.length ? existing : suggested);
                await setSkeleton(confirmed);
                logStandard({
                    type: 'INFO',
                    event: 'skeleton.confirmed',
                    message: `${getSkeleton().length} tópicos`,
                });
            }
        }
        goToChat(t.name, formatExisting);
        // Estado inicial do badge: valida o documento recém-preparado. No fluxo
        // "Formatar" pulamos (a revisão abaixo valida ao terminar).
        if (!formatExisting)
            scheduleValidation();
        if (formatted)
            await announceAndReviewFormatting(t.name, formatted);
    }
    catch (e) {
        console.error('[selectTemplate] falha:', e);
        enableCards();
        setSub(e instanceof FormatError ? e.message : 'Não consegui abrir esse modelo. Tente de novo.');
    }
}
/** Falha do "Formatar" com a frase que o usuário deve ver. */
class FormatError extends Error {
}
/**
 * "Formatar" um documento existente no tipo `t`, sem perder conteúdo.
 *
 * Duas versões anteriores perdiam: uma pedia ao modelo para reescrever o
 * documento inteiro num turno (estourava o tempo no meio e deixava danos que
 * ninguém relatava — QA BUG-01); a outra colava só o texto de volta (tabelas no
 * fim, sem figuras, quebras, listas nem negrito). Agora o documento fica onde
 * está: o template entra na frente dele (`formatIntoTemplate`) e o conteúdo vai
 * para o padrão da marca POR CÓDIGO (`formatDocumentToHouseStyle`). O modelo
 * entra depois, só onde há julgamento, uma seção por turno.
 *
 * A cópia do arquivo inteiro vem antes de tudo: qualquer falha no caminho
 * devolve o documento exatamente como estava, nunca pela metade.
 */
async function formatExistingDocument(t, setSub) {
    setSub('Lendo o documento…');
    let original;
    try {
        original = await captureDocumentFile();
    }
    catch (e) {
        console.error('[formatExistingDocument] sem cópia do documento:', e);
        throw new FormatError('Não consegui fazer uma cópia do documento antes de formatar — por segurança, não mexi nele. Tente de novo.');
    }
    const capa = await readOriginalCover();
    try {
        setSub(`Aplicando ${t.name}…`);
        const transfer = await formatIntoTemplate(t.id, capa);
        setSub('Formatando no padrão da marca…');
        const { report } = await formatDocumentToHouseStyle((feito, total) => setSub(`Formatando no padrão da marca (${feito} de ${total})…`));
        // Vazios que a norma não quer (e o que falta depois de tabela), como no
        // fim de todo turno que escreve.
        await normalizeSpacing();
        logStandard({
            type: 'INFO',
            event: 'format.done',
            detail: {
                cover_removed: transfer.removedCover,
                sections: transfer.contentSections,
                cover_filled: transfer.coverFilled.length,
                styled: report.styled,
                lists: report.lists,
                tables: report.tables,
            },
        });
        return { transfer, report, original };
    }
    catch (e) {
        console.error('[formatExistingDocument] falhou; devolvendo o original:', e);
        logStandard({ type: 'ERROR', event: 'format.failed', message: redactToolError(String(e)) });
        try {
            await restoreDocumentFile(original, t.id);
        }
        catch (r) {
            console.error('[formatExistingDocument] não consegui devolver o original:', r);
            throw new FormatError('Não consegui formatar, e também não consegui devolver o documento sozinho — use Ctrl+Z no Word.');
        }
        throw new FormatError('Não consegui formatar este documento — ele voltou exatamente como estava.');
    }
}
// ======================================================================
// Tela do esqueleto: plano de tópicos do documento
// ======================================================================
// Aparece entre o seletor de tipo e o chat, SÓ para tipos que declaram
// `_default_skeleton` no rules.json. Confirmar não escreve nada no documento:
// grava o plano como metadado (word/skeleton.ts) e entra no contexto de todo
// turno (buildUserTurnText). O plano não é regra — o usuário pode mudá-lo na
// conversa, e o modelo tem `set_document_skeleton` para refletir a mudança.
/**
 * Sincroniza o Confirmar com a contagem de tópicos VÁLIDOS (vazios não contam,
 * porque não são salvos). Abaixo do mínimo o botão fica apagado, mas continua
 * clicável de propósito: `disabled` nativo não dispara clique, e sem clique não
 * há como explicar por que ele não avança. O clique mostra o balão.
 */
function syncSkeletonConfirm(topics) {
    const confirmBtn = document.getElementById('skeleton-confirm');
    if (!confirmBtn)
        return;
    const enough = normalizeSkeleton(topics).length >= SKELETON_MIN_TOPICS;
    confirmBtn.setAttribute('aria-disabled', enough ? 'false' : 'true');
    confirmBtn.title = enough ? '' : `Selecione pelo menos ${SKELETON_MIN_TOPICS} tópicos`;
}
/** Balão de aviso da tela do esqueleto (mesmo `.toast` do composer). */
function showSkeletonToast(msg) {
    const foot = document.querySelector('.skeleton-foot');
    if (!foot)
        return;
    let toast = foot.querySelector('.toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.className = 'toast';
        foot.appendChild(toast);
    }
    toast.textContent = msg;
    if (skeletonToastTimer)
        window.clearTimeout(skeletonToastTimer);
    skeletonToastTimer = window.setTimeout(() => toast === null || toast === void 0 ? void 0 : toast.remove(), 3500);
}
let skeletonToastTimer = null;
/** Ícones das ações da linha (verde soma, laranja subtrai — cor vem do CSS). */
const PLUS_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
const MINUS_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>';
/**
 * Esqueleto sugerido pelo TIPO (`_default_skeleton.topics` do rules.json —
 * prefixo `_` porque é config do painel e NÃO deve entrar no prompt: o que o
 * modelo vê é o plano confirmado, via contexto do turno).
 * `null` = o tipo não declara esqueleto ⇒ a tela do esqueleto não aparece. É o que
 * mantém a feature data-driven: habilitar para um tipo novo é acrescentar a
 * chave no rules.json dele, sem tocar em código.
 */
async function loadSuggestedSkeleton(typeId) {
    try {
        const rules = await readRulesJson(typeId);
        const block = rules === null || rules === void 0 ? void 0 : rules._default_skeleton;
        if (!block)
            return null;
        const topics = normalizeSkeleton(block.topics);
        return topics.length ? topics : null;
    }
    catch (e) {
        console.warn('[loadSuggestedSkeleton] falhou; segue sem a tela de esqueleto:', e);
        return null;
    }
}
/**
 * Renderiza as linhas do editor a partir do array. A numeração é DERIVADA da
 * posição (nunca digitada), então adicionar ou remover no meio renumera tudo
 * sozinho — é o que o usuário espera de um plano de tópicos.
 *
 * `focusIndex` põe o cursor numa linha específica depois de redesenhar (fluxo de
 * "adicionei um tópico, quero digitar nele agora").
 */
function renderSkeletonEditor(topics, focusIndex = null) {
    const listEl = document.getElementById('skeleton-list');
    if (!listEl)
        return;
    listEl.innerHTML = '';
    syncSkeletonConfirm(topics);
    if (!topics.length) {
        // Um (+) precisa sobrar aqui: quem removeu todos os tópicos ficaria sem
        // caminho de volta (o botão "Adicionar tópico" do rodapé foi removido).
        const empty = document.createElement('div');
        empty.className = 'skeleton-empty';
        const addFirst = document.createElement('button');
        addFirst.type = 'button';
        addFirst.className = 'skeleton-act skeleton-act-add';
        addFirst.title = 'Adicionar tópico';
        addFirst.setAttribute('aria-label', 'Adicionar tópico');
        addFirst.innerHTML = PLUS_ICON;
        addFirst.onclick = () => {
            topics.push('');
            renderSkeletonEditor(topics, 0);
        };
        const text = document.createElement('span');
        text.textContent =
            'Sem tópicos no plano. Adicione um, ou siga em Pular e defina a estrutura conversando.';
        empty.appendChild(addFirst);
        empty.appendChild(text);
        listEl.appendChild(empty);
        return;
    }
    topics.forEach((topic, index) => {
        const row = document.createElement('div');
        row.className = 'skeleton-row';
        row.setAttribute('role', 'listitem');
        const num = document.createElement('span');
        num.className = 'skeleton-num';
        num.textContent = `${index + 1}.`;
        num.setAttribute('aria-hidden', 'true');
        row.appendChild(num);
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'skeleton-input';
        input.value = topic;
        input.maxLength = SKELETON_TOPIC_MAX_CHARS;
        input.placeholder = 'Nome do tópico';
        input.setAttribute('aria-label', `Tópico ${index + 1}`);
        // Espelha no array a cada tecla: o array é a fonte da verdade, não o DOM.
        input.oninput = () => {
            topics[index] = input.value;
            // Digitar/apagar muda a contagem de tópicos válidos: o Confirmar precisa
            // acompanhar sem esperar um redesenho.
            syncSkeletonConfirm(topics);
        };
        input.onkeydown = (ev) => {
            if (ev.key === 'Enter') {
                ev.preventDefault();
                if (topics.length >= DEFAULT_SKELETON_MAX_TOPICS)
                    return;
                topics.splice(index + 1, 0, '');
                renderSkeletonEditor(topics, index + 1);
            }
            else if (ev.key === 'Backspace' && input.value === '' && topics.length > 1) {
                // Linha vazia + Backspace = remove e volta para a anterior (como em
                // qualquer editor de lista).
                ev.preventDefault();
                topics.splice(index, 1);
                renderSkeletonEditor(topics, Math.max(0, index - 1));
            }
        };
        row.appendChild(input);
        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'skeleton-act skeleton-act-add';
        addBtn.title = 'Adicionar tópico abaixo';
        addBtn.setAttribute('aria-label', `Adicionar tópico depois de ${index + 1}`);
        addBtn.innerHTML = PLUS_ICON;
        addBtn.onclick = () => {
            if (topics.length >= DEFAULT_SKELETON_MAX_TOPICS)
                return;
            topics.splice(index + 1, 0, '');
            renderSkeletonEditor(topics, index + 1);
        };
        row.appendChild(addBtn);
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'skeleton-act skeleton-act-remove';
        removeBtn.title = 'Remover tópico';
        removeBtn.setAttribute('aria-label', `Remover tópico ${index + 1}`);
        removeBtn.innerHTML = MINUS_ICON;
        removeBtn.onclick = () => {
            topics.splice(index, 1);
            renderSkeletonEditor(topics, Math.min(index, topics.length - 1));
        };
        row.appendChild(removeBtn);
        listEl.appendChild(row);
    });
    if (focusIndex !== null) {
        const inputs = listEl.querySelectorAll('.skeleton-input');
        const target = inputs[Math.max(0, Math.min(focusIndex, inputs.length - 1))];
        if (target) {
            target.focus();
            target.setSelectionRange(target.value.length, target.value.length);
        }
    }
}
/**
 * Mostra a tela do esqueleto e resolve com os tópicos confirmados. O array de
 * trabalho é uma CÓPIA: o usuário mexe à vontade sem tocar no default do tipo.
 */
function askSkeleton(typeName, suggested) {
    return new Promise((resolve) => {
        const topics = [...suggested];
        const eyebrow = document.getElementById('skeleton-eyebrow');
        const title = document.getElementById('skeleton-title');
        const sub = document.getElementById('skeleton-sub');
        const skipBtn = document.getElementById('skeleton-skip');
        const confirmBtn = document.getElementById('skeleton-confirm');
        if (eyebrow)
            eyebrow.textContent = typeName;
        if (title)
            title.textContent = `Defina o esqueleto inicial de tópicos da sua ${typeName}`;
        if (sub) {
            sub.textContent =
                'Esse esqueleto é apenas inicial: a estrutura dos tópicos pode ser alterada depois.';
        }
        renderSkeletonEditor(topics);
        setView('skeleton');
        // Solta os handlers ao sair: a tela é reutilizável e um clique antigo não
        // pode resolver a Promise de uma sessão anterior.
        const finish = (result) => {
            var _a;
            if (skipBtn)
                skipBtn.onclick = null;
            if (confirmBtn)
                confirmBtn.onclick = null;
            if (skeletonToastTimer)
                window.clearTimeout(skeletonToastTimer);
            (_a = document.querySelector('.skeleton-foot .toast')) === null || _a === void 0 ? void 0 : _a.remove();
            resolve(result);
        };
        // Pular = seguir SEM plano. Devolve lista vazia: nenhum bloco de esqueleto
        // entra no contexto e a estrutura fica em aberto para o copiloto.
        if (skipBtn)
            skipBtn.onclick = () => finish([]);
        // Confirmar normaliza: tópico que o usuário abriu com (+) e deixou em
        // branco é DESCARTADO aqui (normalizeSkeleton filtra vazios) — e é por isso
        // que a contagem do mínimo usa a lista normalizada, não as linhas na tela.
        if (confirmBtn) {
            confirmBtn.onclick = () => {
                const clean = normalizeSkeleton(topics);
                if (clean.length < SKELETON_MIN_TOPICS) {
                    showSkeletonToast(`Selecione pelo menos ${SKELETON_MIN_TOPICS} tópicos`);
                    return;
                }
                finish(clean);
            };
        }
    });
}
/** Troca para a tela de chat e mostra a saudação com o tipo escolhido. */
function goToChat(typeName, autoFormatting = false) {
    // Toda conversa nasce com um id: é ele que faz o autosave atualizar UMA linha
    // em vez de criar uma por turno.
    conversationId = newConversationId();
    transcript = [];
    conversationStarted = false;
    lastSavedFingerprint = null;
    resumeNote = null;
    setConversationTitleLocal(null); // conversa nova: título destravado até a 1ª mensagem
    setView('chatting');
    startDocumentWatcher();
    documentTypeName = typeName; // guardado para re-saudar em "Nova conversa"
    reflectDocumentTypeTag();
    setStatus('');
    greet(typeName, autoFormatting);
    promptEl.focus();
}
let pendingAsk = null;
/** Placeholder normal do composer (restaurado ao destravar). */
const PROMPT_PLACEHOLDER = 'Peça uma ação ou @ um tópico…';
const askPanelEl = () => document.getElementById('ask-panel');
const askInputEl = () => document.getElementById('ask-input');
/** Mostra/esconde o campo normal — o painel de esclarecimento toma o lugar dele. */
function setComposerBoxVisible(visible) {
    const wrap = document.querySelector('.composer-input-wrap');
    const hint = document.querySelector('.composer-hint');
    if (wrap)
        wrap.hidden = !visible;
    if (hint)
        hint.hidden = !visible;
}
/** Abre o painel na primeira pergunta da rodada. */
function openAskPanel(req, anchorEl) {
    const panel = askPanelEl();
    if (!panel)
        return;
    pendingAsk = { req, anchorEl, index: 0, answers: [] };
    setComposerBoxVisible(false);
    panel.hidden = false;
    showCurrentQuestion();
}
/**
 * Mostra a pergunta `pendingAsk.index`. Rótulos são a cópia FIXA do tipo (o
 * modelo não escreve botão); o texto da pergunta é markdown sanitizado — o
 * modelo pode destacar o dado pedido e listar as alternativas do manual.
 */
function showCurrentQuestion() {
    const ask = pendingAsk;
    const input = askInputEl();
    const tag = document.getElementById('ask-tag');
    const question = document.getElementById('ask-question');
    const primary = document.getElementById('ask-primary');
    const secondary = document.getElementById('ask-secondary');
    if (!ask || !input || !tag || !question || !primary || !secondary)
        return;
    const { kind, questions } = ask.req;
    const texto = questions[ask.index];
    const labels = ASK_LABELS[kind];
    tag.textContent = askTagText(kind, ask.index, questions.length);
    question.innerHTML = renderMarkdown(texto);
    primary.textContent = labels.primary;
    secondary.textContent = labels.secondary;
    // `scope`/`offtopic` são confirmações de duas vias: não há o que digitar.
    input.hidden = kind !== 'clarify';
    input.value = '';
    autoGrowAsk();
    // Registra a pergunta no log dobrável (é o único lugar em que ela fica).
    appendClarifyEntry(ask.anchorEl, 'assistant', texto);
    if (kind === 'clarify')
        input.focus();
    else
        primary.focus();
}
function closeAskPanel() {
    const panel = askPanelEl();
    const input = askInputEl();
    if (panel)
        panel.hidden = true;
    if (input)
        input.value = '';
    pendingAsk = null;
    setComposerBoxVisible(true);
}
/**
 * A resposta pode ser longa: o campo cresce até o teto do CSS e só então rola.
 * `scrollHeight` mede conteúdo + padding, SEM borda; como o campo é
 * `box-sizing: border-box`, atribuí-lo direto deixa a caixa 2px curta e o
 * textarea nasce rolando à toa. `offsetHeight - clientHeight` é exatamente essa
 * borda.
 */
const ASK_INPUT_MAX_PX = 220;
function autoGrowAsk() {
    const input = askInputEl();
    if (!input)
        return;
    input.style.height = 'auto';
    const border = input.offsetHeight - input.clientHeight;
    input.style.height = Math.min(input.scrollHeight + border, ASK_INPUT_MAX_PX) + 'px';
}
/**
 * Registra a resposta à pergunta em exibição e avança: mostra a próxima ou, na
 * última, encerra a rodada mandando todas as respostas juntas ao modelo.
 */
function answerClarify(answer) {
    const ask = pendingAsk;
    if (!ask || ask.req.kind !== 'clarify')
        return;
    ask.answers.push({ question: ask.req.questions[ask.index], answer });
    appendClarifyEntry(ask.anchorEl, 'user', answer !== null && answer !== void 0 ? answer : 'Ainda não sei');
    ask.index += 1;
    if (ask.index < ask.req.questions.length) {
        showCurrentQuestion();
        return;
    }
    const semResposta = ask.answers.filter((a) => a.answer === null).length;
    void finishAsk(ASK_REPLIES.answers(ask.answers), {
        questions: ask.answers.length,
        unknown: semResposta,
    });
}
/**
 * Encerra a rodada e dispara o turno seguinte com o texto que o modelo precisa
 * ler. `promptText` nulo = nada a enviar ("Não, vou separar por partes").
 */
async function finishAsk(promptText, detail = {}) {
    const ask = pendingAsk;
    if (!ask)
        return;
    const anchorEl = ask.anchorEl;
    const kind = ask.req.kind;
    closeAskPanel();
    logStandard({
        type: 'INFO',
        event: 'clarify.answered',
        detail: Object.assign({ kind, answered: promptText !== null }, detail),
    });
    // "Não, vou separar por partes": nada é enviado. A mensagem original do
    // usuário continua no chat, e ele reescreve o pedido do jeito que preferir.
    if (promptText === null) {
        setStatus('');
        return;
    }
    setBusy(true);
    try {
        // "Sim, colocar" na confirmação de conteúdo desconexo vai no contrato: o
        // planejador, lendo só o "sim", voltava a perguntar.
        await runAgentLoop(promptText, [], anchorEl, { scopeConfirmed: kind === 'offtopic' });
    }
    finally {
        setBusy(false);
        currentAbort = null;
    }
}
function wireAskPanel() {
    const input = askInputEl();
    const primary = document.getElementById('ask-primary');
    const secondary = document.getElementById('ask-secondary');
    if (!input || !primary || !secondary)
        return;
    input.addEventListener('input', autoGrowAsk);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            primary.click();
        }
    });
    primary.onclick = () => {
        const ask = pendingAsk;
        if (!ask)
            return;
        if (isConfirmKind(ask.req.kind)) {
            appendClarifyEntry(ask.anchorEl, 'user', ASK_LABELS[ask.req.kind].primary);
            void finishAsk(ask.req.kind === 'offtopic' ? ASK_REPLIES.offtopicYes : ASK_REPLIES.scopeYes);
            return;
        }
        const text = input.value.trim();
        if (!text) {
            showComposerToast('Escreva a informação ou toque em "Ainda não sei".');
            return;
        }
        // No log aparece o que o usuário escreveu; ao modelo as respostas vão
        // juntas e enquadradas, depois da última pergunta (ver ASK_REPLIES.answers).
        answerClarify(text);
    };
    secondary.onclick = () => {
        const ask = pendingAsk;
        if (!ask)
            return;
        // "Não": nada é enviado — o pedido fica no chat, e o documento como estava.
        if (isConfirmKind(ask.req.kind)) {
            appendClarifyEntry(ask.anchorEl, 'user', ASK_LABELS[ask.req.kind].secondary);
            void finishAsk(null);
            return;
        }
        answerClarify(null);
    };
}
/** Ícone do botão que abre/fecha o log (gira 180° quando aberto — só CSS). */
const CHEVRON_ICON = '<svg class="chev" viewBox="0 0 24 24" width="9" height="9" fill="none" ' +
    'stroke="currentColor" stroke-width="2.6" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
/**
 * Acrescenta uma linha ao log dobrável da mensagem `anchorEl`, criando o log na
 * primeira vez. A pergunta do mIAu é markdown sanitizado, como no painel; a
 * resposta do usuário é texto puro, como toda mensagem dele.
 */
function appendClarifyEntry(anchorEl, who, text) {
    var _a;
    let log = anchorEl.querySelector('.clarify-log');
    let entries;
    let toggle;
    if (!log) {
        log = document.createElement('div');
        log.className = 'clarify-log';
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'clarify-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        entries = document.createElement('div');
        entries.className = 'clarify-entries';
        entries.hidden = true;
        toggle.onclick = () => {
            const open = entries.hidden;
            entries.hidden = !open;
            toggle.setAttribute('aria-expanded', String(open));
            scrollToEnd();
        };
        log.append(toggle, entries);
        ((_a = anchorEl.querySelector('.bubble')) !== null && _a !== void 0 ? _a : anchorEl).appendChild(log);
    }
    else {
        toggle = log.querySelector('.clarify-toggle');
        entries = log.querySelector('.clarify-entries');
    }
    const entry = document.createElement('div');
    entry.className = 'clarify-entry ' + who;
    const label = document.createElement('span');
    label.className = 'clarify-who';
    label.textContent = who === 'assistant' ? 'mIAu' : 'Você';
    const body = document.createElement('div');
    body.className = 'clarify-text';
    if (who === 'assistant')
        body.innerHTML = renderMarkdown(text);
    else
        body.textContent = text;
    entry.append(label, body);
    entries.appendChild(entry);
    // O contador conta PERGUNTAS: é a unidade que o usuário reconhece.
    const asked = entries.querySelectorAll('.clarify-entry.assistant').length;
    const count = Math.max(asked, 1);
    toggle.innerHTML = CHEVRON_ICON;
    const caption = document.createElement('span');
    caption.textContent = count === 1 ? '1 esclarecimento' : count + ' esclarecimentos';
    toggle.appendChild(caption);
    scrollToEnd();
}
// ============================================================================
// Sessão do usuário: login corporativo, retomada e salvamento
// ============================================================================
// Três coisas que andam juntas e por isso moram no mesmo lugar:
//
//  1. o login (SSO do Office/Entra) é a PORTA: sem conta corporativa válida o
//     painel não abre — não há modo reduzido;
//  2. a retomada, que devolve o chat E o documento de uma conversa anterior;
//  3. o salvamento automático, que alimenta a retomada ao fim de cada turno.
//
// O e-mail nunca é decidido aqui. Quem valida o token e diz quem é o usuário é
// o backend (`GET /me`); o painel só repassa o token e mostra o resultado.
/** Usuário confirmado pelo backend. `null` = dev sem SSO configurado. */
let identity = null;
/** O backend tem banco de conversas? Sem ele não há o que restaurar nem salvar. */
let storageEnabled = false;
/** Conversa ATUAL. Todo salvamento usa este id, então uma conversa é uma linha. */
let conversationId = null;
/** Bolhas na tela, em ordem — é isto que a restauração devolve (ver util/session.ts). */
let transcript = [];
/**
 * A conversa já teve algum turno (pedido do usuário, formatação automática) ou
 * foi retomada? Antes disso nada é gravado: uma conversa que só tem a saudação
 * não merece linha no banco — e, com a retenção das N últimas, ocuparia o lugar
 * de uma conversa de verdade.
 */
let conversationStarted = false;
/** Impressão digital do documento no último salvamento COM documento. */
let lastSavedFingerprint = null;
function newConversationId() {
    const c = globalThis.crypto;
    return (c === null || c === void 0 ? void 0 : c.randomUUID) ? c.randomUUID() : `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
/**
 * Tela de bloqueio. Fica fora do `#app` porque não existe painel sem
 * identidade: nem chat, nem seletor de tipo, nem "modo visitante".
 */
/** Contagem regressiva ativa do botão da tela de bloqueio. */
let authGateTimer = null;
/**
 * Aviso para quem é do diretório mas está fora do grupo de acesso (o backend
 * responde 403). Cópia FIXA do painel: o texto que a pessoa lê não depende do
 * que o servidor mandar.
 */
const ACCESS_DENIED_TEXT = 'Desculpe, você não tem acesso por enquanto.';
const AUTH_GATE_TITLE = 'Entre com a sua conta corporativa';
function showAuthGate(detail, retryable, retryAfterMs = 0, title = AUTH_GATE_TITLE) {
    const host = document.getElementById('auth-gate');
    const msg = document.getElementById('auth-gate-detail');
    const retry = document.getElementById('auth-gate-retry');
    if (!host || !msg)
        return;
    if (authGateTimer !== null)
        window.clearInterval(authGateTimer);
    const titulo = document.getElementById('auth-gate-title');
    if (titulo)
        titulo.textContent = title;
    msg.textContent = detail;
    msg.hidden = !detail;
    if (retry) {
        retry.hidden = !retryable;
        retry.onclick = () => {
            host.hidden = true;
            void bootSession();
        };
        // Espera obrigatória (hoje só o throttling do Office pede uma). Sem travar o
        // botão, a reação natural — clicar de novo na hora — é exatamente o que
        // renova a punição, e o usuário fica preso num ciclo que ele mesmo alimenta.
        if (retryable && retryAfterMs > 0) {
            let restante = Math.ceil(retryAfterMs / 1000);
            const rotulo = () => `Tentar de novo (${restante}s)`;
            retry.disabled = true;
            retry.textContent = rotulo();
            authGateTimer = window.setInterval(() => {
                restante -= 1;
                if (restante > 0) {
                    retry.textContent = rotulo();
                    return;
                }
                window.clearInterval(authGateTimer);
                authGateTimer = null;
                retry.disabled = false;
                retry.textContent = 'Tentar de novo';
                // A pausa passou: tenta sozinho. O usuário está esperando por isso, e
                // exigir um clique depois de mandar aguardar seria pedir duas vezes.
                host.hidden = true;
                void bootSession();
            }, 1000);
        }
        else {
            retry.disabled = false;
            retry.textContent = 'Tentar de novo';
        }
    }
    host.hidden = false;
    document.getElementById('app').hidden = true;
    document.getElementById('boot-error').hidden = true;
}
/**
 * Garante um usuário autenticado antes de qualquer outra coisa.
 *
 * O `GET /auth/config` vem primeiro de propósito: pedir token ao Office num
 * backend que nem tem SSO configurado abre um erro do host sem explicação. Com
 * a configuração em mãos, o painel sabe se deve pedir o token, e sabe se a
 * ausência dele bloqueia (produção) ou apenas segue sem e-mail (dev).
 */
async function signIn() {
    var _a, _b, _c, _d;
    let cfg;
    try {
        cfg = await getAuthConfig();
    }
    catch (e) {
        // Backend fora do ar já foi tratado antes (showBackendError); chegar aqui
        // significa uma versão antiga do backend, sem a rota. Segue sem identidade.
        dlog('[signIn] /auth/config indisponível:', e);
        return true;
    }
    storageEnabled = cfg.storage_enabled;
    if (!cfg.sso_configured) {
        if (cfg.auth_required) {
            showAuthGate('O login corporativo ainda não foi configurado nesta instalação do mIAu. ' +
                'Avise quem administra a ferramenta.', false);
            return false;
        }
        dlog('[signIn] SSO não configurado; seguindo sem identidade (dev)');
        return true;
    }
    try {
        await acquireToken();
        identity = await getMe();
        storageEnabled = identity.storage_enabled;
        logStandard({
            type: 'INFO',
            event: 'auth.signed_in',
            // `has_name` e não o nome: é o suficiente para saber se a saudação da
            // tela de retomada vai existir, sem levar dado de pessoa para o log.
            // Nem todo diretório preenche a claim `name`.
            detail: {
                authenticated: identity.authenticated,
                storage: storageEnabled,
                has_name: Boolean(firstName(identity.name)),
            },
        });
        return true;
    }
    catch (e) {
        const auth = e instanceof AuthError ? e : null;
        const api = e instanceof ApiError ? e : null;
        const detalhe = (_b = (_a = auth === null || auth === void 0 ? void 0 : auth.message) !== null && _a !== void 0 ? _a : api === null || api === void 0 ? void 0 : api.message) !== null && _b !== void 0 ? _b : 'Não consegui verificar a sua conta corporativa. Tente de novo.';
        logStandard({
            type: 'ERROR',
            event: 'auth.failed',
            message: detalhe.slice(0, 200),
            detail: { code: auth === null || auth === void 0 ? void 0 : auth.code, status: api === null || api === void 0 ? void 0 : api.status, retry_after_ms: auth === null || auth === void 0 ? void 0 : auth.retryAfterMs },
        });
        if ((api === null || api === void 0 ? void 0 : api.status) === 403) {
            // Do diretório, mas fora do grupo de acesso: o backend recusa toda rota,
            // inclusive em dev. Sem "Tentar de novo" — outro token não muda nada; quem
            // for incluído no grupo entra ao reabrir o Word.
            showAuthGate('', false, 0, ACCESS_DENIED_TEXT);
            return false;
        }
        if (!cfg.auth_required) {
            // Dev: o backend aceita sem token. Bloquear aqui impediria de trabalhar
            // numa máquina que não está logada no Office com a conta da empresa.
            dlog('[signIn] login falhou, mas AUTH_REQUIRED=false; seguindo:', detalhe);
            return true;
        }
        showAuthGate(detalhe, (_c = auth === null || auth === void 0 ? void 0 : auth.retryable) !== null && _c !== void 0 ? _c : true, (_d = auth === null || auth === void 0 ? void 0 : auth.retryAfterMs) !== null && _d !== void 0 ? _d : 0);
        return false;
    }
}
// ---------------------------------------------------------------------------
// Política de Uso
// ---------------------------------------------------------------------------
/**
 * Chave do aceite no armazenamento local. Guarda a VERSÃO aceita, não um
 * booleano: publicar uma política nova (subir `POLICY_VERSION` no backend) tem
 * que fazer o texto voltar, e um `true` antigo esconderia o texto novo.
 */
/**
 * Chave local do aceite, POR USUÁRIO: `miau.policy.accepted.<hash do e-mail>`.
 * A chave única de antes valia para o perfil do Windows inteiro — quem entrasse
 * depois no mesmo Office pulava a política. O hash evita deixar o e-mail em
 * claro no armazenamento do navegador; a chave antiga, sem usuário, é ignorada.
 */
function policyStorageKey(email) {
    let h = 0x811c9dc5; // FNV-1a 32 bits: basta separar usuários, não é segredo
    for (const ch of (email || 'sem-login').toLowerCase()) {
        h ^= ch.charCodeAt(0);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `miau.policy.accepted.${h.toString(16)}`;
}
function storedPolicyVersion(key) {
    try {
        return localStorage.getItem(key);
    }
    catch (_a) {
        return null; // storage bloqueado no host: mostra de novo, que é o lado seguro
    }
}
/** Tenta gravar o aceite no servidor; `true` só se ele confirmar que gravou. */
async function recordPolicyOnServer() {
    try {
        return (await acceptPolicy()).recorded;
    }
    catch (e) {
        dlog('[policy] não consegui registrar o aceite no servidor:', e);
        return false;
    }
}
/**
 * Mostra a Política de Uso até o usuário aceitar — e só na primeira vez.
 *
 * Quem decide é o BACKEND (`policy_accepted` do `/me`, gravado por e-mail e
 * versão): assim a pessoa que troca de máquina não vê o texto de novo, e fica
 * registrado quem aceitou o quê e quando. O armazenamento local é a rede de
 * segurança para os casos em que não há essa resposta — dev sem SSO, banco
 * desligado, backend antigo —, e evita que o painel peça o aceite a cada
 * abertura.
 *
 * Na dúvida, MOSTRA: começar a usar sem nunca ter lido a política é pior do que
 * ler duas vezes.
 */
async function ensurePolicyAccepted() {
    const modal = document.getElementById('policy-modal');
    const aceitar = document.getElementById('policy-accept');
    if (!modal || !aceitar)
        return;
    const versao = (identity === null || identity === void 0 ? void 0 : identity.policy_version) || 'local';
    if (identity === null || identity === void 0 ? void 0 : identity.policy_accepted)
        return;
    const chaveLocal = policyStorageKey(identity === null || identity === void 0 ? void 0 : identity.email);
    if (storedPolicyVersion(chaveLocal) === versao) {
        // Aceite desta pessoa guardado só aqui (banco desligado ou fora do ar na
        // hora). Sem servidor que grave, vale o local; com servidor, registra agora
        // — ela já aceitou ESTA versão — em vez de o aceite nunca chegar ao banco.
        if (!(identity === null || identity === void 0 ? void 0 : identity.storage_enabled))
            return;
        if (await recordPolicyOnServer()) {
            logStandard({ type: 'SUCCESS', event: 'policy.accepted', detail: { policy_version: versao, recorded: true, late: true } });
        }
        return;
    }
    logStandard({ type: 'INFO', event: 'policy.shown', detail: { policy_version: versao } });
    modal.hidden = false;
    aceitar.focus();
    await new Promise((resolve) => {
        aceitar.onclick = () => {
            aceitar.onclick = null;
            modal.hidden = true;
            resolve();
        };
    });
    try {
        localStorage.setItem(chaveLocal, versao);
    }
    catch (_a) {
        /* sem storage: o backend continua sendo a memória do aceite */
    }
    // Fora do caminho crítico: o usuário já leu e aceitou. Se o servidor não
    // gravar agora, o aceite local desta pessoa é reenviado na próxima abertura.
    const gravado = await recordPolicyOnServer();
    logStandard({ type: 'SUCCESS', event: 'policy.accepted', detail: { policy_version: versao, recorded: gravado } });
}
/**
 * Porta de entrada do painel: login, Política de Uso e, só então, a tela
 * inicial.
 *
 * Separado do `setupAddIn` porque o botão "Tentar de novo" da tela de bloqueio
 * repete exatamente esta parte — não faria sentido refazer o wiring do DOM.
 */
async function bootSession() {
    document.getElementById('app').hidden = false;
    if (!(await signIn()))
        return;
    await ensurePolicyAccepted();
    await showEntryScreen();
}
// ---------------------------------------------------------------------------
// Retomada
// ---------------------------------------------------------------------------
/**
 * Primeira tela depois do login: sempre a tela inicial (`renderRestoreScreen`),
 * com a lista de conversas salvas quando há alguma. Falha ao consultar o banco
 * não vira tela de erro: a tela inicial aparece do mesmo jeito, avisando que o
 * histórico está indisponível — ficar preso por causa dele seria pior do que
 * começar do zero.
 */
async function showEntryScreen() {
    let salvas = [];
    let historicoDisponivel = storageEnabled;
    if (storageEnabled) {
        try {
            salvas = await listConversations();
        }
        catch (e) {
            // Falha ao consultar não vira tela de erro: a tela de boas-vindas aparece
            // do mesmo jeito, só sem oferecer retomada. Ficar preso por causa do
            // histórico seria pior do que começar do zero.
            dlog('[showEntryScreen] não consegui listar as conversas:', e);
            historicoDisponivel = false;
        }
    }
    logStandard({
        type: 'INFO',
        event: 'entry.screen',
        detail: { screen: 'welcome', saved: salvas.length, history: historicoDisponivel },
    });
    renderRestoreScreen(salvas, historicoDisponivel);
}
const TRASH_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3"/></svg>';
/**
 * Cabeçalho da tela de retomada — saúda pelo nome quando dá.
 *
 * Esta é a PRIMEIRA tela do painel, e o único lugar onde o nome da pessoa
 * aparece: é o retorno visível de ter feito login com a conta da empresa. Sem
 * nome utilizável, usa um título neutro que depende de haver conversas, em vez
 * de um "Olá, ." — o diretório nem sempre preenche a claim `name`.
 */
function renderRestoreHeader(temConversas, historicoDisponivel) {
    const nome = firstName(identity === null || identity === void 0 ? void 0 : identity.name);
    const tituloEl = document.getElementById('restore-title');
    const subEl = document.getElementById('restore-sub');
    // Sem nome utilizável, um título neutro — nem todo diretório preenche a claim
    // `name`, e "Olá, ." seria pior do que não saudar.
    if (tituloEl) {
        tituloEl.textContent = nome
            ? `Olá, ${nome}.`
            : temConversas
                ? 'Retomar de onde parou?'
                : 'Que documento vamos escrever?';
    }
    if (!subEl)
        return;
    if (temConversas) {
        // Só a pergunta (texto pedido pelo produto). O aviso de que retomar pode
        // trocar o documento aberto saiu daqui, mas a proteção é do fluxo: a
        // retomada só troca o documento quando precisa, e confirma antes (ver
        // `decideDocumentOnRestore`).
        subEl.textContent =
            'Sou seu assistente de documentos, gostaria de retomar alguma conversa ou começar uma nova?';
    }
    else if (historicoDisponivel) {
        subEl.textContent =
            'Você ainda não tem conversas salvas. Quando terminar uma, ela aparece ' +
                'aqui para você retomar depois.';
    }
    else {
        // Persistência desligada ou banco fora do ar: dizer isso é melhor do que
        // deixar a pessoa achando que as conversas dela sumiram.
        subEl.textContent =
            'O histórico de conversas não está disponível agora — dá para trabalhar ' +
                'normalmente, mas esta sessão não será salva para retomar depois.';
    }
}
/**
 * Tela de entrada do painel — a PRIMEIRA coisa que aparece depois do login.
 *
 * Aparece sempre, mesmo sem nenhuma conversa salva: é ela que saúda pelo nome e
 * que oferece criar uma conversa. Pular direto para o seletor de tipo quando não
 * há histórico faria a experiência mudar de forma conforme o estado do banco —
 * às vezes uma tela, às vezes outra —, e a pessoa nunca saberia onde o histórico
 * dela vai estar.
 */
function renderRestoreScreen(salvas, historicoDisponivel = true) {
    setView('restoring');
    renderRestoreHeader(salvas.length > 0, historicoDisponivel);
    const listEl = document.getElementById('restore-list');
    listEl.innerHTML = '';
    // Sem conversas, a ação de criar ocupa o CENTRO da tela em vez de um botão no
    // rodapé: o miolo do painel ficaria vazio, e a única coisa a fazer ali estaria
    // na periferia, pequena. Aqui ela é o próprio conteúdo.
    listEl.classList.toggle('is-empty', salvas.length === 0);
    if (!salvas.length) {
        const cta = document.createElement('button');
        cta.type = 'button';
        cta.className = 'restore-cta';
        cta.innerHTML =
            '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" ' +
                'stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
                '<path d="M12 5v14M5 12h14"/></svg>';
        const rotulo = document.createElement('span');
        rotulo.textContent = 'Criar uma conversa';
        cta.appendChild(rotulo);
        cta.onclick = () => void showTemplatePicker();
        listEl.appendChild(cta);
    }
    for (const conversa of salvas) {
        const row = document.createElement('div');
        row.className = 'restore-row';
        row.setAttribute('role', 'listitem');
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'restore-card';
        const nome = document.createElement('span');
        nome.className = 'restore-name';
        nome.textContent = conversa.title || 'Conversa sem título';
        card.appendChild(nome);
        const meta = document.createElement('span');
        meta.className = 'restore-meta';
        meta.textContent = summarizeConversation(conversa);
        card.appendChild(meta);
        card.onclick = () => void openSavedConversation(conversa);
        row.appendChild(card);
        // Exclusão manual (a retenção automática só corta o excedente das últimas).
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'restore-del';
        del.title = 'Apagar esta conversa';
        del.setAttribute('aria-label', `Apagar “${conversa.title}”`);
        del.innerHTML = TRASH_ICON;
        del.onclick = (ev) => {
            ev.stopPropagation(); // o card inteiro é clicável: não abrir ao apagar
            void removeSavedConversation(conversa, row);
        };
        row.appendChild(del);
        listEl.appendChild(row);
    }
    // O rodapé é a ação secundária ao lado da lista. Sem lista não há "ao lado":
    // a ação já está no centro, e repeti-la aqui seria o mesmo botão duas vezes.
    const foot = document.getElementById('restore-foot');
    if (foot)
        foot.hidden = salvas.length === 0;
    const novo = document.getElementById('restore-new');
    if (novo)
        novo.onclick = () => void showTemplatePicker();
}
/** Apaga uma conversa salva e tira a linha da lista. */
async function removeSavedConversation(conversa, row) {
    try {
        await deleteConversation(conversa.id);
    }
    catch (e) {
        // O clique no lixo é uma ação DELIBERADA: engolir a falha faz o card
        // continuar na tela sem explicação, e a pessoa clica de novo achando que
        // errou a mira. Diz o que houve e registra.
        dlog('[removeSavedConversation] falhou:', e);
        logStandard({
            type: 'ERROR',
            event: 'conversation.delete_failed',
            message: redactToolError(String(e)),
        });
        const sub = document.getElementById('restore-sub');
        if (sub)
            sub.textContent = 'Não consegui apagar essa conversa agora. Tente de novo.';
        return;
    }
    row.remove();
    logStandard({ type: 'INFO', event: 'conversation.deleted' });
    // Última conversa apagada: a tela continua, mas vira o estado vazio — com a
    // ação de criar no centro, como em qualquer abertura sem histórico.
    if (!document.querySelectorAll('#restore-list .restore-card').length) {
        renderRestoreScreen([]);
    }
}
/** "em 28/09 às 14:32" a partir do ISO gravado; vazio se não houver data. */
function savedWhen(iso) {
    if (!iso)
        return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '';
    const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return ` em ${dia} às ${hora}`;
}
/**
 * Modal da retomada. A ação principal vem em tinta cheia; a alternativa (quando
 * há) e o Cancelar, fantasmas. Devolve qual foi escolhida.
 */
function askRestoreChoice(opts) {
    var _a;
    const modal = document.getElementById('restore-modal');
    const titulo = document.getElementById('restore-modal-title');
    const texto = document.getElementById('restore-modal-text');
    const principal = document.getElementById('restore-modal-confirm');
    const alternativa = document.getElementById('restore-modal-alt');
    const cancelar = document.getElementById('restore-modal-cancel');
    if (titulo)
        titulo.textContent = opts.title;
    texto.textContent = opts.text;
    principal.textContent = opts.primary;
    if (alternativa) {
        alternativa.hidden = !opts.alt;
        alternativa.textContent = (_a = opts.alt) !== null && _a !== void 0 ? _a : '';
    }
    return new Promise((resolve) => {
        const fechar = (escolha) => {
            modal.hidden = true;
            principal.onclick = null;
            cancelar.onclick = null;
            if (alternativa)
                alternativa.onclick = null;
            resolve(escolha);
        };
        principal.onclick = () => fechar('primary');
        cancelar.onclick = () => fechar('cancel');
        if (alternativa)
            alternativa.onclick = () => fechar('alt');
        modal.hidden = false;
        principal.focus();
    });
}
/**
 * O que fazer com o documento aberto ao retomar — decidido ANTES de tocar no
 * Word.
 *
 * A retomada trocava SEMPRE o documento aberto pelo gravado. Isso apagava o que
 * o banco não tinha visto — um Desfazer, edições manuais —, e o QA perdeu
 * trabalho assim, sem aviso. Agora depende de QUAL documento está aberto:
 *  - o desta conversa (vínculo `miau.conversation`), com o mesmo texto do
 *    salvamento: segue com ele, sem pergunta;
 *  - o desta conversa, mas diferente: pergunta, com "manter como está" em
 *    primeiro — o arquivo aberto é o estado mais recente que o usuário viu;
 *  - outro documento, vazio: traz o gravado — não há o que perder;
 *  - outro documento, com conteúdo: confirma a sobrescrita, como antes.
 */
async function decideDocumentOnRestore(conversa) {
    var _a;
    const salvo = (_a = conversa.document_ooxml) !== null && _a !== void 0 ? _a : null;
    const vinculada = await readLinkedConversationId();
    if (vinculada === conversa.id) {
        if (!salvo)
            return { action: 'keep', changed: false };
        const meta = snapshotMeta(salvo);
        const atual = await readDocumentFingerprint();
        // Impressão desconhecida (salvamento antigo, host sem o texto) é "não sei":
        // pergunta. Nunca se presume igual.
        if (atual && meta.fingerprint && atual === meta.fingerprint) {
            return { action: 'keep', changed: false };
        }
        const escolha = await askRestoreChoice({
            title: 'Este documento mudou desde o último salvamento',
            text: `É o documento desta conversa, mas ele está diferente da versão que salvei` +
                `${savedWhen(meta.savedAt)} — por edições feitas direto no Word ou por um Desfazer, ` +
                'por exemplo. Com qual versão você quer continuar?',
            primary: 'Manter o documento como está',
            alt: 'Voltar à versão salva',
        });
        return {
            action: escolha === 'primary' ? 'keep' : escolha === 'alt' ? 'replace' : 'cancel',
            changed: true,
        };
    }
    if (!salvo) {
        const escolha = await askRestoreChoice({
            title: 'Esta conversa foi salva sem documento',
            text: `“${conversa.title}” foi salva sem documento: volta só a conversa, e o arquivo aberto fica como está.`,
            primary: 'Abrir a conversa',
        });
        return { action: escolha === 'primary' ? 'keep' : 'cancel', changed: false };
    }
    // Documento em branco: trazer o gravado não apaga nada de ninguém.
    if (!(await documentHasContent()))
        return { action: 'replace', changed: false };
    const escolha = await askRestoreChoice({
        title: 'Isto substitui o documento aberto',
        text: `Vou trocar o conteúdo deste documento pelo que estava salvo em “${conversa.title}”. ` +
            'O que estiver aqui agora será perdido.',
        primary: 'Restaurar mesmo assim',
    });
    return { action: escolha === 'primary' ? 'replace' : 'cancel', changed: false };
}
/**
 * Clique numa conversa salva: carrega, decide o documento e só então retoma.
 * Checagens ANTES de tocar no documento: abortar depois de sobrescrever o
 * arquivo do usuário seria o pior dos dois mundos.
 */
async function openSavedConversation(resumo) {
    const listEl = document.getElementById('restore-list');
    const travarLista = (travada) => listEl.querySelectorAll('button').forEach((b) => (b.disabled = travada));
    travarLista(true);
    let conversa;
    try {
        conversa = await loadConversation(resumo.id);
        if (!conversa.document_type) {
            // Sem tipo não há regras, e o composer fica bloqueado (`send()` exige
            // `documentType`). Só aconteceria com uma linha gravada por uma versão
            // anterior; melhor recomeçar pelo seletor do que abrir um chat inerte.
            throw new Error('conversa salva sem tipo de documento');
        }
    }
    catch (e) {
        restoreFailed(e);
        return;
    }
    const escolha = await decideDocumentOnRestore(conversa);
    logStandard({
        type: 'INFO',
        event: 'conversation.restore_choice',
        detail: { action: escolha.action, changed: escolha.changed },
    });
    if (escolha.action === 'cancel') {
        travarLista(false);
        return;
    }
    await restoreConversation(conversa, escolha);
}
/** Falha ao abrir: volta a lista ao normal e diz o que houve. */
function restoreFailed(e) {
    var _a;
    console.error('[restoreConversation] falhou:', e);
    (_a = document
        .getElementById('restore-list')) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button').forEach((b) => (b.disabled = false));
    const sub = document.getElementById('restore-sub');
    if (sub)
        sub.textContent = 'Não consegui abrir essa conversa. Tente outra ou comece uma nova.';
    logStandard({
        type: 'ERROR',
        event: 'conversation.restore_failed',
        message: redactToolError(String(e)),
    });
}
/**
 * Traz de volta o chat — e, se a decisão foi essa, o documento — de uma
 * conversa salva.
 *
 * Ordem importa: o documento primeiro. Se a escrita no Word falhar, paramos
 * antes de mexer no estado do painel — melhor continuar na lista do que ficar
 * com um chat que fala de um documento que não está aberto.
 */
async function restoreConversation(conversa, escolha) {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        if (templateCatalog.length === 0) {
            try {
                templateCatalog = await loadTemplateCatalog();
            }
            catch (_h) {
                /* artigo da saudação cai no default "a" */
            }
        }
        const documentType_ = conversa.document_type;
        // O documento é restaurado à parte, e a falha dele NÃO derruba o resto: o
        // chat é o que sempre dá para trazer de volta, e perdê-lo junto por causa de
        // um OOXML corrompido troca uma perda parcial por uma total. Visto na
        // prática — um blob inválido no banco fazia o `insertOoxml` levantar
        // `GeneralException` e a tela dizia só "não consegui abrir essa conversa",
        // sem restaurar nada, embora as mensagens estivessem íntegras.
        let documentoVoltou = true;
        let documentoTrocado = false;
        if (escolha.action === 'replace' && conversa.document_ooxml) {
            try {
                await restoreDocumentFile(conversa.document_ooxml, documentType_);
                documentoTrocado = true;
            }
            catch (e) {
                documentoVoltou = false;
                console.error('[restoreConversation] documento não voltou:', e);
                logStandard({
                    type: 'WARNING',
                    event: 'conversation.document_restore_failed',
                    message: redactToolError(String(e)),
                    detail: {
                        ooxml_chars: conversa.document_ooxml.length,
                        format: isDocxSnapshot(conversa.document_ooxml) ? 'docx' : 'body_ooxml',
                    },
                });
            }
        }
        conversationId = conversa.id;
        // Título volta TRAVADO no que já estava salvo — senão o autosave do
        // próximo turno recalcularia via deriveTitle e apagaria um rename antigo
        // (ou o nome que a própria 1ª mensagem já tinha gerado antes).
        setConversationTitleLocal(conversa.title || null);
        documentType = documentType_;
        documentTypeName = (_a = conversa.document_type_name) !== null && _a !== void 0 ? _a : '';
        // Bug antigo: o subtítulo (tipo do documento) só era escrito no fluxo de
        // conversa NOVA (goToChat) — toda retomada ficava com o header preso no
        // texto genérico, independente do tipo real. Corrigido aqui.
        reflectDocumentTypeTag();
        selectedRules = await loadRules(documentType_, documentTypeName || documentType_);
        await setSkeleton((_b = conversa.skeleton) !== null && _b !== void 0 ? _b : []);
        // O documento aberto passa a ser o desta conversa — inclusive quando veio de
        // um salvamento anterior ao vínculo, que não traz a propriedade.
        if (documentoVoltou)
            await linkDocumentToConversation(conversa.id);
        conversationStarted = true;
        // O que o banco tem: a impressão digital do gravado quando ele voltou; a do
        // documento aberto quando ele foi mantido sem mudança. Mantido COM mudança,
        // o banco está atrás — salvar logo abaixo o acerta.
        lastSavedFingerprint = documentoTrocado
            ? (_c = snapshotMeta(conversa.document_ooxml).fingerprint) !== null && _c !== void 0 ? _c : null
            : escolha.changed
                ? null
                : await readDocumentFingerprint();
        // Mantido com mudança: o histórico fala de um documento que já não é
        // exatamente este — o modelo é avisado no primeiro turno.
        resumeNote =
            escolha.action === 'keep' && escolha.changed
                ? 'Esta conversa foi retomada numa nova sessão, e o documento aberto tem alterações ' +
                    'feitas fora do chat desde o último salvamento. O outline acima é o estado atual; ' +
                    'releia antes de editar regiões que você conhecia.'
                : null;
        // O histórico do modelo só volta se estiver íntegro (ver historyIsResumable).
        // Quando não está, a conversa continua NA TELA e o modelo recomeça limpo —
        // é a degradação que o usuário entende, em vez de um erro no próximo envio.
        const resumivel = historyIsResumable(conversa.messages);
        conversationHistory = resumivel ? conversa.messages : [];
        // Arquivos não são gravados com a conversa (só o texto): a retomada volta
        // sem eles. Se o pedido depender de um, o modelo pede para reanexar.
        conversationAttachments = [];
        renderConversationAttachments();
        messagesEl.innerHTML = '';
        transcript = [];
        for (const entry of (_d = conversa.transcript) !== null && _d !== void 0 ? _d : []) {
            addMessage(entry.role, entry.text);
        }
        // Duas coisas podem falhar de forma independente, e a mensagem tem que
        // dizer a verdade sobre cada uma: afirmar "o documento voltou como estava"
        // quando ele não voltou é pior que não dizer nada.
        const avisos = [];
        if (!documentoVoltou) {
            avisos.push('não consegui trazer de volta o documento salvo — o que está aberto agora continua como estava');
        }
        if (!resumivel) {
            avisos.push('perdi o fio da meada da última resposta, então me lembre do que precisa a seguir');
        }
        if (avisos.length) {
            addMessage('assistant', `Retomei esta conversa, mas ${avisos.join('; e ')}.`);
        }
        // Pedido que ficou pela metade (o Word ou o painel fechou no meio do turno):
        // antes ele simplesmente sumia. Agora a conversa diz que ele não terminou e
        // oferece refazê-lo com um toque.
        const interrompido = interruptedTurnEntry((_e = conversa.transcript) !== null && _e !== void 0 ? _e : []);
        if (interrompido) {
            const doUsuario = interrompido.role === 'user';
            const aviso = addMessage('assistant', doUsuario
                ? 'Seu último pedido não terminou: o Word ou o painel fechou no meio dele. O documento ' +
                    'pode ter ficado com parte das alterações — confira antes de refazer.'
                : 'A formatação automática não terminou: o Word ou o painel fechou no meio dela. O que ' +
                    'foi formatado até ali ficou no documento.');
            turnActionsOf(aviso).appendChild(turnActionButton(doUsuario ? 'Refazer o pedido' : 'Continuar a formatação', doUsuario
                ? interrompido.text
                : 'Continue a formatação do documento de onde parou: o conteúdo já está no ' +
                    'documento; formate o que falta, sem reescrever o que já foi formatado.'));
            logStandard({
                type: 'WARNING',
                event: 'conversation.interrupted_turn_found',
                detail: { from_user: doUsuario },
            });
        }
        // Estado de turno anterior não sobrevive à retomada: falhas, snapshot de
        // "Desfazer" e a assinatura do documento são todos daquela sessão.
        lastTurnFailure = null;
        documentRevertedNote = false;
        docStateAtTurnEnd = null;
        removeUndo();
        removeContinue();
        undoSnapshot = null;
        setView('chatting');
        // As bolhas foram desenhadas com `#messages` ainda escondido (a tela era
        // "restoring" até a linha acima) — o `scrollToEnd()` de dentro de cada
        // `addMessage()` rodou sobre um `scrollHeight` sem sentido de um elemento
        // com `display:none`. Repete aqui, agora que o CSS já mostra `#messages`,
        // para abrir de fato na última mensagem em vez do topo do histórico.
        scrollToEnd();
        setStatus('');
        startDocumentWatcher();
        scheduleValidation();
        // Banco atrás do que está na tela (documento mantido com mudança, ou o aviso
        // de pedido interrompido que acabou de entrar na conversa): grava agora.
        if (escolha.changed || interrompido)
            scheduleAutosave();
        logStandard({
            type: 'SUCCESS',
            event: 'conversation.restored',
            detail: {
                message_count: (_g = (_f = conversa.transcript) === null || _f === void 0 ? void 0 : _f.length) !== null && _g !== void 0 ? _g : 0,
                // o que REALMENTE voltou, não o que estava guardado
                with_document: documentoTrocado,
                kept_open_document: escolha.action === 'keep',
                document_format: conversa.document_ooxml
                    ? isDocxSnapshot(conversa.document_ooxml) ? 'docx' : 'body_ooxml'
                    : null,
                history_resumed: resumivel,
            },
        });
        promptEl.focus();
    }
    catch (e) {
        restoreFailed(e);
    }
}
// ---------------------------------------------------------------------------
// Salvamento
// ---------------------------------------------------------------------------
/**
 * Salva depois de um instante de silêncio.
 *
 * O fim de um turno dispara faxina de espaçamento e validação em background; se
 * o snapshot saísse junto, gravaríamos o documento antes da faxina — e a
 * restauração traria de volta exatamente os parágrafos vazios que acabaram de
 * ser removidos. A espera também colapsa turnos rápidos em sequência num
 * salvamento só.
 */
let autosaveTimer = null;
const AUTOSAVE_DELAY_MS = 2000;
function scheduleAutosave() {
    if (!storageEnabled || !conversationId || !conversationStarted)
        return;
    if (autosaveTimer !== null)
        window.clearTimeout(autosaveTimer);
    autosaveTimer = window.setTimeout(() => {
        autosaveTimer = null;
        void persistConversation();
    }, AUTOSAVE_DELAY_MS);
}
/**
 * Grava agora o que estava só agendado.
 *
 * A espera de 2s existe para o documento salvo bater com o que o usuário vê
 * (a faxina de espaçamento roda logo depois do turno). Mas ela abre uma janela:
 * fechar o painel dentro dela levava a última conversa junto, porque o
 * `setTimeout` morre com a página. Aqui a janela é fechada no melhor esforço
 * possível — sem o snapshot do documento, que é a parte cara e a que pode não
 * caber no tempo que o navegador dá a uma página que está saindo.
 */
function flushAutosave() {
    if (autosaveTimer === null)
        return;
    window.clearTimeout(autosaveTimer);
    autosaveTimer = null;
    void persistConversation({ comDocumento: false });
}
function wireAutosaveFlush() {
    // `visibilitychange` para `hidden` é o sinal que o navegador garante numa aba
    // que some; `pagehide` cobre o fechamento de fato. `beforeunload` não entra:
    // no WebView do Office ele não é confiável e ainda pode segurar o fechamento.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
            flushAutosave();
    });
    window.addEventListener('pagehide', flushAutosave);
}
/**
 * O documento como vai para o banco: o .docx INTEIRO. O OOXML do corpo, que era
 * o gravado antes, não leva seção, cabeçalho e rodapé, e na volta os estilos do
 * documento de destino vencem os dele — a conversa retomada abria com a capa e
 * o corpo desformatados. Se o host não entregar o arquivo, grava o corpo mesmo:
 * a restauração sabe recompor esse formato sobre o template do tipo.
 */
async function snapshotForConversation(meta) {
    try {
        return await captureDocumentFile(meta);
    }
    catch (e) {
        logStandard({
            type: 'WARNING',
            event: 'conversation.document_capture_fallback',
            message: redactToolError(String(e)),
        });
        return snapshotDocument();
    }
}
/**
 * Salvamentos em FILA: cada um lê o estado no momento em que roda, e o próximo
 * só começa quando o anterior terminou. Sem isso, o salvamento do início do
 * turno (sem documento, rápido) e o do fim (com documento, lento) podiam chegar
 * ao banco fora de ordem — e o mais velho venceria, com o pedido ainda marcado
 * como "em andamento".
 */
let saveQueue = Promise.resolve();
/** Grava o estado atual do painel. Falha nunca chega ao usuário. */
function persistConversation({ comDocumento = true } = {}) {
    const vez = saveQueue.then(() => doPersistConversation(comDocumento));
    saveQueue = vez.catch(() => undefined);
    return vez;
}
async function doPersistConversation(comDocumento) {
    if (!storageEnabled || !conversationId || !conversationStarted || !transcript.length)
        return;
    // O estado da CONVERSA é lido já: capturar o documento leva tempo, e uma
    // retomada no meio trocaria o chat por outro sob este mesmo id.
    const id = conversationId;
    // "Travado": só deriva de novo enquanto `conversationTitle` ainda é null
    // (conversa nova, sem 1ª mensagem salva ainda). A partir daí — tenha vindo
    // do deriveTitle ou de um rename manual — o valor gravado aqui é sempre o
    // mesmo que já está na tela; autosave nunca mais pisa em cima do nome.
    if (conversationTitle === null) {
        setConversationTitleLocal(deriveTitle(transcript, documentTypeName || 'Conversa'));
    }
    const estado = {
        title: conversationTitle !== null && conversationTitle !== void 0 ? conversationTitle : deriveTitle(transcript, documentTypeName || 'Conversa'),
        document_type: documentType,
        document_type_name: documentTypeName || null,
        skeleton: getSkeleton(),
        messages: conversationHistory,
        transcript: transcript.map((e) => (Object.assign({}, e))),
    };
    try {
        let ooxml = null;
        let impressao = null;
        if (comDocumento) {
            // O documento passa a dizer de qual conversa é: é o que permite à
            // retomada manter o arquivo reaberto em vez de sobrescrevê-lo.
            await linkDocumentToConversation(id);
            impressao = await readDocumentFingerprint();
            ooxml = await snapshotForConversation({
                fingerprint: impressao !== null && impressao !== void 0 ? impressao : undefined,
                savedAt: new Date().toISOString(),
            });
            // Trocou de conversa durante a captura: esse arquivo é da outra.
            if (conversationId !== id)
                ooxml = null;
        }
        await saveConversation(id, Object.assign(Object.assign({}, estado), { document_ooxml: ooxml, 
            // Sem documento capturado (salvamento de início de turno, painel
            // fechando, captura que falhou): o backend mantém o que já estava.
            keep_document: !ooxml }));
        if (ooxml && impressao)
            lastSavedFingerprint = impressao;
        dlog('[persistConversation] salvo', { com_documento: !!ooxml });
    }
    catch (e) {
        // Salvamento é conveniência para o USUÁRIO: quebrar o chat porque o banco
        // piscou seria trocar um problema invisível por um visível. Mas silêncio
        // TOTAL foi o que deixou um erro de CORS derrubar todo autosave por dias —
        // o painel não reclamava e o banco ficava vazio. Agora a falha vai para a
        // telemetria: invisível na tela, visível para quem opera.
        dlog('[persistConversation] falhou (ignorado):', e);
        logStandard({
            type: 'ERROR',
            event: 'conversation.save_failed',
            message: redactToolError(String(e)),
            // `bubbles`, e não `messages`: o canal standard PROÍBE a chave `messages`
            // (lá ela é o histórico do modelo, que não pode ir para log) — com o nome
            // colidindo, o próprio aviso de falha voltava 422 e nunca era gravado.
            detail: { with_document: comDocumento, bubbles: estado.transcript.length },
        });
    }
}
// ---------------------------------------------------------------------------
// Turno em andamento e edições fora do chat
// ---------------------------------------------------------------------------
/**
 * Início de turno: marca a bolha que abriu o pedido como EM ANDAMENTO e grava
 * já, sem documento (rápido). Se o Word fechar no meio, a conversa volta com o
 * pedido e o aviso de que ele não terminou — antes o pedido simplesmente
 * sumia, porque só o fim do turno gravava.
 */
function beginTurnPersistence(entryIndex) {
    conversationStarted = true;
    for (const e of transcript)
        delete e.pending;
    if (transcript[entryIndex])
        transcript[entryIndex].pending = true;
    void persistConversation({ comDocumento: false });
}
/** Fim de turno (qualquer desfecho): nenhum pedido segue "em andamento". */
function endTurnPersistence() {
    for (const e of transcript)
        delete e.pending;
}
/**
 * Vigia de edições FORA do chat. O banco só recebia o documento no fim de um
 * turno, então edições manuais e Ctrl+Z não chegavam lá — e uma retomada em
 * outro documento trazia a versão velha. A cada intervalo, com o painel
 * ocioso, compara a impressão digital do texto com a do último salvamento;
 * mudou e PAROU de mudar (duas leituras iguais), salva com documento. Assim
 * não se salva no meio da digitação.
 */
const WATCH_INTERVAL_MS = 30000;
let watchTimer = null;
let observedFingerprint = null;
function startDocumentWatcher() {
    if (watchTimer !== null)
        return;
    watchTimer = window.setInterval(() => void watchDocumentTick(), WATCH_INTERVAL_MS);
}
function panelIsIdle() {
    var _a;
    const view = (_a = document.getElementById('app')) === null || _a === void 0 ? void 0 : _a.dataset.view;
    const modalAberto = !!document.querySelector('.modal-overlay:not([hidden])');
    return (view === 'chatting' &&
        !currentAbort &&
        !pendingAsk &&
        !modalAberto &&
        autosaveTimer === null &&
        document.visibilityState === 'visible');
}
async function watchDocumentTick() {
    if (!storageEnabled || !conversationId || !conversationStarted || !panelIsIdle())
        return;
    const atual = await readDocumentFingerprint();
    if (!atual || atual === lastSavedFingerprint) {
        observedFingerprint = atual;
        return;
    }
    if (atual !== observedFingerprint) {
        observedFingerprint = atual; // ainda mudando: espera estabilizar
        return;
    }
    if (!panelIsIdle())
        return; // um turno pode ter começado durante a leitura
    logStandard({ type: 'INFO', event: 'conversation.document_changed_outside' });
    await persistConversation({ comDocumento: true });
}
/** Nome do tipo escolhido (para saudar ao entrar na conversa). */
let documentTypeName = '';
/**
 * Botão "Feedback" do header: abre o formulário de chamados (`URL_TICKET`).
 *
 * Abre no NAVEGADOR do usuário, via `Office.context.ui.openBrowserWindow` — o
 * formulário é uma página da intranet, com login próprio, e o painel do Word é
 * uma janela estreita e sem barra de endereço. Em host que não suporte a API,
 * `window.open` resolve. Sem URL configurada o botão nem aparece: botão que não
 * leva a lugar nenhum é pior que botão nenhum.
 */
function wireFeedbackButton() {
    const btn = document.getElementById('feedback-btn');
    if (!btn)
        return;
    if (!__TICKET_URL__) {
        btn.remove();
        return;
    }
    btn.hidden = false;
    btn.onclick = () => {
        var _a, _b, _c;
        try {
            const suportado = (_b = (_a = Office.context.requirements) === null || _a === void 0 ? void 0 : _a.isSetSupported) === null || _b === void 0 ? void 0 : _b.call(_a, 'OpenBrowserWindowApi', '1.1');
            if (suportado && ((_c = Office.context.ui) === null || _c === void 0 ? void 0 : _c.openBrowserWindow)) {
                Office.context.ui.openBrowserWindow(__TICKET_URL__);
            }
            else {
                window.open(__TICKET_URL__, '_blank', 'noopener');
            }
            logStandard({ type: 'INFO', event: 'feedback.opened' });
        }
        catch (e) {
            // Nada de erro silencioso: o usuário clicou para reportar um problema.
            console.error('[feedback] não consegui abrir o formulário:', e);
            showComposerToast('Não consegui abrir o formulário. Copie o endereço: ' + __TICKET_URL__);
        }
    };
}
// ----------------------------------------------------------------------------
// Título da conversa: mostra no header (nome + tipo de documento embaixo) e
// permite renomear.
//
// "Travado" quer dizer: uma vez que `conversationTitle` tem um valor (derivado
// da 1ª mensagem OU escolhido pelo usuário), `persistConversation` para de
// recalculá-lo a cada autosave — sem isso, editar o nome era inútil: o
// próximo salvamento automático (a cada turno) sobrescrevia com
// `deriveTitle(transcript, ...)` de novo. `null` é o único estado "destravado"
// (conversa nova, ainda sem 1ª mensagem); a TELA mostra "Nova Conversa" nesse
// estado, mas isso é só exibição — o valor guardado continua `null` até a 1ª
// mensagem ou um rename explícito travarem de vez.
//
// Edição É DUAS exibições que se alternam por `hidden` (nunca as duas juntas):
// o <span> (leitura) e o <input> (edição), que só aparece no clique do lápis —
// diferente do balão de esclarecimento e de outros campos do painel, aqui não
// existe "clique no texto para editar": o texto some, o campo aparece.
// ----------------------------------------------------------------------------
const NEW_CONVERSATION_TITLE_PLACEHOLDER = 'Nova Conversa';
let conversationTitle = null;
/** Consumida pelo handler de `blur` do título — distingue Enter/blur normal
 *  (salva) de Escape (cancela), sem duplicar o caminho de saída da edição. */
let titleEditCancelled = false;
function chatTitleEls() {
    return {
        display: document.getElementById('chat-title-display'),
        input: document.getElementById('chat-title-input'),
        editBtn: document.getElementById('chat-title-edit'),
        typeTag: document.getElementById('chat-type-tag'),
    };
}
/** Sincroniza o <span> de leitura com `conversationTitle` (o <input> só existe
 *  enquanto em edição — ver enterTitleEditMode). */
function reflectConversationTitle() {
    const { display } = chatTitleEls();
    if (display)
        display.textContent = conversationTitle || NEW_CONVERSATION_TITLE_PLACEHOLDER;
}
/** Mostra o tipo de documento como subtítulo — SEMPRE, conversa nova ou
 *  retomada (ao contrário do `#brand-tag` antigo, que só era atualizado no
 *  fluxo de conversa nova e ficava preso no texto genérico em qualquer
 *  retomada — bug real, corrigido aqui). */
function reflectDocumentTypeTag() {
    const { typeTag } = chatTitleEls();
    if (typeTag)
        typeTag.textContent = documentTypeName;
}
/** Define o título "de dentro" (autosave derivando, ou restauração) — não
 *  chama a rota; quem grava é `persistConversation`/`restoreConversation`. */
function setConversationTitleLocal(title) {
    conversationTitle = title;
    reflectConversationTitle();
}
function enterTitleEditMode() {
    const { display, input } = chatTitleEls();
    if (!display || !input)
        return;
    // Começa vazio (não com o placeholder "Nova Conversa"): é rótulo de tela,
    // nunca foi um valor de verdade, e obrigar a apagá-lo primeiro seria atrito
    // à toa.
    input.value = conversationTitle !== null && conversationTitle !== void 0 ? conversationTitle : '';
    display.hidden = true;
    input.hidden = false;
    input.focus();
    input.select();
}
function exitTitleEditMode(save) {
    const { display, input } = chatTitleEls();
    if (!display || !input)
        return;
    if (save)
        void commitConversationTitle(input.value);
    input.hidden = true;
    display.hidden = false;
}
/** Confirma a edição do usuário: atualiza local e manda o PATCH leve.
 *  Vazio (ou sem mudança) é ignorado — mesma regra de `deriveTitle`: título
 *  vazio não identifica nada numa lista de conversas. */
async function commitConversationTitle(raw) {
    const novo = raw.trim().slice(0, TITLE_MAX_CHARS);
    if (!novo || novo === conversationTitle) {
        reflectConversationTitle(); // resincroniza o <span> (descarta edição vazia)
        return;
    }
    setConversationTitleLocal(novo);
    if (!storageEnabled || !conversationId)
        return; // renomeia só localmente até salvar de vez
    try {
        await renameConversation(conversationId, novo);
        logStandard({ type: 'INFO', event: 'conversation.renamed' });
    }
    catch (e) {
        // 404 é esperado: a conversa ainda não foi salva (nenhum turno terminou
        // ainda), então não existe linha no banco para o PATCH mirar. O próximo
        // autosave grava este título via PUT normalmente — nada quebrou, só não
        // tem o que atualizar agora. Qualquer OUTRO erro é avisado e vai para a
        // telemetria (mesmo padrão do resto do arquivo: silêncio total já
        // escondeu falha real antes).
        const notFound = e instanceof ApiError && e.status === 404;
        if (!notFound) {
            showComposerToast('Não consegui salvar o novo título agora — tente de novo em instantes.');
        }
        logStandard({
            type: notFound ? 'INFO' : 'ERROR',
            event: 'conversation.rename_failed',
            message: redactToolError(String(e)),
        });
    }
}
function wireConversationTitle() {
    const { editBtn, input } = chatTitleEls();
    if (editBtn)
        editBtn.onclick = () => enterTitleEditMode();
    if (!input)
        return;
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            input.blur(); // um caminho só de saída: o handler de blur decide salvar
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            titleEditCancelled = true;
            input.blur();
        }
    });
    input.addEventListener('blur', () => {
        const cancelado = titleEditCancelled;
        titleEditCancelled = false;
        exitTitleEditMode(!cancelado);
    });
}
// --- Tema claro/escuro: botão sol/lua do header -----------------------------
// Por padrão o painel NÃO fixa tema: segue o Office (prefers-color-scheme, ou o
// data-theme que o script do taskpane.html escreve em hosts que não propagam).
// Ao clicar, o usuário fixa um tema — e essa escolha passa a valer sobre o host,
// inclusive nas próximas aberturas do painel.
const THEME_KEY = 'miau.theme';
/** Lê a escolha fixada (localStorage pode estar bloqueado no host). */
function storedTheme() {
    try {
        const v = localStorage.getItem(THEME_KEY);
        return v === 'light' || v === 'dark' ? v : null;
    }
    catch (_a) {
        return null;
    }
}
/** Tema que está valendo AGORA: o fixado, senão o que o host/SO informam. */
function effectiveTheme() {
    var _a;
    const pinned = document.documentElement.dataset.theme;
    if (pinned === 'light' || pinned === 'dark')
        return pinned;
    return ((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
}
/** Só rótulo/estado do botão — não fixa tema nenhum. */
function syncThemeToggle(theme) {
    const btn = document.getElementById('theme-toggle');
    if (!btn)
        return;
    btn.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
    btn.title = theme === 'dark' ? 'Tema escuro — clique para o claro' : 'Tema claro — clique para o escuro';
}
function wireThemeToggle() {
    const btn = document.getElementById('theme-toggle');
    if (!btn)
        return;
    const pinned = storedTheme();
    if (pinned)
        document.documentElement.dataset.theme = pinned;
    syncThemeToggle(effectiveTheme());
    btn.onclick = () => {
        const next = effectiveTheme() === 'dark' ? 'light' : 'dark';
        document.documentElement.dataset.theme = next;
        try {
            localStorage.setItem(THEME_KEY, next);
        }
        catch (_a) {
            // Sem storage a troca vale só para esta sessão do painel — melhor que nada.
        }
        syncThemeToggle(next);
    };
}
/**
 * Modal "documento já tem conteúdo": resolve com a escolha do usuário.
 * `cancel` (Esc / clique no fundo) volta para o seletor.
 */
function askContentAction() {
    return new Promise((resolve) => {
        const overlay = document.getElementById('content-modal');
        const clearBtn = document.getElementById('modal-clear');
        const formatBtn = document.getElementById('modal-format');
        const close = (result) => {
            overlay.hidden = true;
            clearBtn.onclick = null;
            formatBtn.onclick = null;
            overlay.onclick = null;
            document.removeEventListener('keydown', onKey);
            resolve(result);
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                close('cancel');
        };
        clearBtn.onclick = () => close('clear');
        formatBtn.onclick = () => close('format');
        overlay.onclick = (e) => {
            if (e.target === overlay)
                close('cancel'); // clique no fundo = cancelar
        };
        document.addEventListener('keydown', onKey);
        overlay.hidden = false;
    });
}
// ============================================================================
// "Formatar": o que foi feito + revisão por seção
// ============================================================================
// A formatação por código já deixou o documento no padrão. O modelo entra só
// onde há julgamento (título sem número, linha em negrito que talvez seja
// título, folha de rosto antiga em texto) — e só nas seções em que um desses
// pontos aparece, uma por turno, com o progresso na linha de status. Cada turno
// cabe folgado no limite de passos: era o documento inteiro num turno só que
// estourava o tempo (QA BUG-01, UX-05).
/** Resumo fiel do que a formatação fez — o que o usuário precisa conferir vem dito. */
function formattingSummary(typeName, transfer, report) {
    const capa = transfer.coverFilled;
    const faltam = ['title', 'client']
        .filter((c) => !capa.includes(c))
        .map((c) => (c === 'title' ? 'o título' : 'o cliente'));
    const linhaCapa = capa.length
        ? `a capa com ${enumerar(capa.map((c) => (c === 'title' ? 'o título' : c === 'client' ? 'o cliente' : 'a data')))} do documento original` +
            (faltam.length ? ` (não achei ${faltam.join(' nem ')} na capa antiga: diga qual é)` : '')
        : 'a capa nova (não achei título nem cliente na capa antiga: diga quais são)';
    const partes = [
        `${plural(report.styled, 'parágrafo', 'parágrafos')} no estilo da marca ` +
            `(${plural(report.headings, 'título', 'títulos')})`,
        report.lists
            ? plural(report.lists, 'lista', 'listas') +
                (report.nestedLists ? ` (${report.nestedLists} com subitens)` : '')
            : '',
        report.tables ? plural(report.tables, 'tabela', 'tabelas') : '',
    ].filter(Boolean);
    const aplicado = articleForType(typeName) === 'o' ? 'aplicado' : 'aplicada';
    return (`**${typeName}** ${aplicado} ao seu documento, sem mexer no conteúdo: ${linhaCapa}; ` +
        `${enumerar(partes)}. Figuras, quebras de página e seções ficaram como estavam.`);
}
/** "1 lista" / "3 listas". */
function plural(n, um, varios) {
    return `${n} ${n === 1 ? um : varios}`;
}
/** "a, b e c". */
function enumerar(itens) {
    return itens.length <= 1 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}
/** Estado da revisão por seção — sobrevive a um Parar, para o Continuar seguir dali. */
let formatReview = null;
/** A mesma seção antes e depois de ganhar número no título. */
function reviewKey(s) {
    return s.headingIndex === null ? '(abertura)' : s.title.replace(/^\s*\d+(?:\.\d+)*\.?\s+/, '').toLowerCase();
}
async function announceAndReviewFormatting(typeName, f) {
    const el = addMessage('assistant', formattingSummary(typeName, f.transfer, f.report));
    attachOriginalRestore(el, f.original);
    let secoes = [];
    try {
        secoes = await planFormattingReview();
    }
    catch (e) {
        console.warn('[announceAndReviewFormatting] sem plano de revisão:', e);
    }
    if (!secoes.length) {
        addMessage('assistant', 'Nada pede revisão: títulos, listas e tabelas já estão no padrão.');
        scheduleValidation();
        return;
    }
    formatReview = { total: secoes.length, done: 0, reviewed: new Set() };
    addMessage('assistant', `Agora reviso ${secoes.length === 1 ? 'a seção que pede' : `as ${secoes.length} seções que pedem`} ` +
        'julgamento (títulos sem número, linhas em negrito que podem ser títulos), uma de cada vez.');
    await reviewFormattedSections();
}
/**
 * Um turno por seção que ainda pede revisão, recalculando a lista antes de
 * cada um (um turno que apaga a folha de rosto antiga desloca os índices das
 * seções seguintes). Para no Parar, num erro ou numa pergunta do modelo, e
 * deixa o botão para continuar dali.
 */
async function reviewFormattedSections() {
    const estado = formatReview;
    if (!estado)
        return;
    for (let guarda = 0; guarda < estado.total + 5; guarda++) {
        let secoes;
        try {
            secoes = await planFormattingReview();
        }
        catch (e) {
            console.warn('[reviewFormattedSections] não consegui reler o documento:', e);
            break;
        }
        const proxima = secoes.find((s) => !estado.reviewed.has(reviewKey(s)));
        if (!proxima)
            break;
        estado.reviewed.add(reviewKey(proxima));
        estado.done++;
        const total = Math.max(estado.total, estado.done);
        statusPrefix = `revisão ${estado.done} de ${total}`;
        // A folha de rosto antiga só sai depois de a capa ter o que ela dizia: o
        // prompt mostra a capa como está AGORA.
        const capa = proxima.issues.some((x) => x.kind === 'capa-antiga') ? await readOriginalCover() : null;
        // E por código: apagar a folha de rosto só passa com o que ela diz na capa.
        setOldCoverSheetGuard(proxima.issues.filter((x) => x.kind === 'capa-antiga').map((x) => x.text));
        setBusy(true);
        try {
            await runAgentLoop(formatReviewPrompt(proxima, estado.done, total, capa));
        }
        finally {
            setOldCoverSheetGuard(null);
            setBusy(false);
            currentAbort = null;
            statusPrefix = '';
            setStatus('');
        }
        await keepOldCoverDate(proxima);
        if (lastTurnStatus === 'CANCELLED' || lastTurnStatus === 'ERROR' || pendingAsk) {
            // Esta seção pode ter ficado pela metade: o Continuar a refaz.
            estado.reviewed.delete(reviewKey(proxima));
            estado.done--;
            const el = addMessage('assistant', `Revisão pausada (${estado.done} de ${total} seções).`);
            attachReviewContinue(el);
            return;
        }
    }
    formatReview = null;
    addMessage('assistant', `Revisão concluída (${estado.done} de ${Math.max(estado.total, estado.done)} seções).`);
}
function formatReviewPrompt(s, i, n, capa = null) {
    const onde = s.headingIndex === null
        ? 'o começo do conteúdo (antes do primeiro título)'
        : `a seção «${s.title}»`;
    const campo = (v) => (v ? `«${v}»` : '(vazio)');
    const capaAgora = capa
        ? `\nA capa agora: título ${campo(capa.title)}, cliente ${campo(capa.client)}, data ${campo(capa.date)}.\n`
        : '';
    return (`Revisão da formatação — ${i} de ${n}: ${onde}, parágrafos ${s.start} a ${s.end}.\n${capaAgora}\n` +
        'O documento JÁ está no padrão da marca: estilos, fontes, listas e tabelas foram aplicados ' +
        'por código, e o conteúdo é o original, no lugar. Falta só o que exige julgamento, e só NESTA ' +
        'seção. Pontos encontrados:\n' +
        `${describeIssues(s.issues)}\n\n` +
        'O que fazer com cada um:\n' +
        '- título sem número: numere no texto conforme a hierarquia e a sequência do documento ' +
        '(veja o outline) — "3. Escopo", "3.1 Premissas" — com replace_paragraphs. Carta de ' +
        'encaminhamento, apresentação e sumário não levam número: deixe como estão.\n' +
        '- linha toda em negrito: se abre um bloco de conteúdo, é título — apply_paragraph_styles com ' +
        'heading_2 ou heading_3; se é destaque dentro do texto, deixe.\n' +
        '- número digitado à mão: se é título disfarçado, estilo de título com o número no texto; se é ' +
        'item de lista, numbered_list.\n' +
        '- estilo de capa no corpo (folha de rosto antiga): PRIMEIRO leve para a capa o que ela diz e a ' +
        'capa não (fill_content_control, tags document_title, client_name e document_date — o cliente e ' +
        'a data exatamente como a folha de rosto os escreve); SÓ DEPOIS apague essas linhas e a quebra de ' +
        'página delas (delete_paragraphs) — a ferramenta recusa enquanto algo delas não estiver na capa, ' +
        'e diz o quê. Se ela tiver qualquer outra informação que não cabe na capa, não apague.\n\n' +
        `Leia antes a janela com read_document_text (startIndex=${s.start}, maxParagraphs=` +
        `${s.end - s.start + 1}). NÃO reescreva, resuma nem mude a ordem do conteúdo, e não mexa fora ` +
        'desta seção. Não pergunte nada: na dúvida, deixe como está. Termine com UMA linha dizendo o ' +
        'que ajustou.');
}
/**
 * Rede da data: se a folha de rosto antiga saiu do documento e a data que ela
 * escrevia não está na capa, leva a data para a capa. Visto com o modelo real:
 * ele levou o cliente e apagou a folha de rosto, e a data ("março de 2026")
 * sumia — a capa ficava com a de hoje (o "data trocada pela atual" do QA).
 */
async function keepOldCoverDate(secao) {
    const comData = secao.issues.filter((x) => x.kind === 'capa-antiga' && x.date);
    if (!comData.length)
        return;
    try {
        const aindaNoDocumento = await Word.run(async (context) => {
            const body = context.document.body;
            body.load('text');
            await context.sync();
            return comData.some((x) => body.text.includes(x.text));
        });
        if (aindaNoDocumento)
            return;
        const data = comData[0].date;
        const capa = await readOriginalCover();
        if (capa.date === data)
            return;
        const r = await executeTool('fill_content_control', { tag: 'document_date', text: data });
        if (r.is_error)
            return;
        addMessage('assistant', `Levei a data da folha de rosto antiga («${data}») para a capa.`);
        logStandard({ type: 'INFO', event: 'format.cover_date_kept' });
    }
    catch (e) {
        console.warn('[keepOldCoverDate] não consegui conferir a data da capa:', e);
    }
}
/** Pílula "Continuar a revisão": segue da seção em que parou. */
function attachReviewContinue(host) {
    const btn = document.createElement('button');
    btn.className = 'turn-action-btn';
    btn.type = 'button';
    btn.textContent = 'Continuar a revisão';
    btn.onclick = () => {
        if (sendBtn.classList.contains('busy') || pendingAsk || !formatReview)
            return;
        dropEmptyTurnActions(btn);
        void reviewFormattedSections();
    };
    turnActionsOf(host).appendChild(btn);
    scrollToEnd();
}
/** "Voltar ao documento original": o arquivo como estava antes de formatar. */
let originalRestoreEl = null;
let originalSnapshot = null;
function attachOriginalRestore(host, snapshot) {
    removeOriginalRestore();
    originalSnapshot = snapshot;
    const btn = document.createElement('button');
    btn.className = 'turn-action-btn';
    btn.type = 'button';
    btn.textContent = 'Voltar ao documento original';
    btn.onclick = () => void restoreOriginalDocument();
    turnActionsOf(host).appendChild(btn);
    originalRestoreEl = btn;
    scrollToEnd();
}
/**
 * Some quando o usuário manda o primeiro pedido: dali em diante, voltar ao
 * original descartaria também o trabalho novo da conversa.
 */
function removeOriginalRestore() {
    if (originalRestoreEl)
        dropEmptyTurnActions(originalRestoreEl);
    originalRestoreEl = null;
    originalSnapshot = null;
}
async function restoreOriginalDocument() {
    const snapshot = originalSnapshot;
    if (!snapshot || sendBtn.classList.contains('busy'))
        return;
    removeOriginalRestore();
    formatReview = null;
    setStatus('voltando ao original');
    try {
        // O snapshot é o .docx inteiro: o tipo só seria usado para um OOXML legado.
        await restoreDocumentFile(snapshot, documentType !== null && documentType !== void 0 ? documentType : 'outro');
        addMessage('assistant', 'Pronto: o documento voltou a ser o original, como estava antes de formatar.');
        // O histórico fala do documento formatado: o próximo turno precisa saber.
        documentRevertedNote = true;
        scheduleValidation();
        scheduleAutosave();
        logStandard({ type: 'INFO', event: 'format.reverted' });
    }
    catch (e) {
        console.error('[restoreOriginalDocument] falhou:', e);
        addMessage('assistant', 'Não consegui voltar ao original automaticamente. Use **Ctrl+Z** no Word.');
        logStandard({ type: 'ERROR', event: 'format.revert_failed', message: redactToolError(String(e)) });
    }
    finally {
        setStatus('');
    }
}
function showBackendError() {
    // O `#app` nasce `hidden` e quem o revela é o `bootSession` — que não roda
    // quando o /health falha. Sem esta linha, o erro apareceria num painel em
    // branco (foi assim que o boot já quebrou antes; ver showBootError).
    document.getElementById('app').hidden = false;
    setView('picking');
    const listEl = document.getElementById('template-list');
    listEl.innerHTML =
        '<p class="picker-sub">Não consegui conectar ao backend. Verifique se a API está rodando e recarregue o painel.</p>';
}
// ============================================================================
// Referências clicáveis (RF04) + menções @tópico / @selecionado
// ----------------------------------------------------------------------------
// As duas features se apoiam no MESMO alicerce: o OUTLINE do documento (os
// parágrafos com estilo de título, via getDocumentOverview). Isso é puramente
// estrutural — depende dos estilos nomeados do Word, não de nenhum rules.json
// de tipo —, então funciona para QUALQUER Tipo de Documento, inclusive os que
// forem adicionados no futuro.
//
// Robustez contra deriva ("versão desatualizada"): nada é resolvido por ÍNDICE
// guardado. Uma referência/menção carrega o TEXTO EXATO do título e é resolvida
// contra o outline VIVO no momento do uso (mesmo princípio do expectedPrefix do
// replace_paragraphs). Se o documento mudou e o título não existe mais, avisamos
// em vez de navegar/editar o lugar errado.
// ============================================================================
/** Normaliza rótulo de título para casar link/menção com o outline vivo:
 *  sem acentos, minúsculo, espaços colapsados, sem ":" final. */
function normLabel(s) {
    return (s || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .replace(/[:：]\s*$/, '')
        .trim()
        .toLowerCase();
}
/** Escapa texto para interpolar com segurança em innerHTML (menu de menções). */
function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}
/** Lê o outline FRESCO (títulos do corpo com índice global). Best-effort. */
async function fetchOutline() {
    var _a;
    try {
        const ov = (await getDocumentOverview());
        return ((_a = ov.outline) !== null && _a !== void 0 ? _a : []).filter((h) => (h.text || '').trim().length > 0);
    }
    catch (_b) {
        return [];
    }
}
let toastTimer;
/** Aviso transitório acima do composer (ex.: referência que não resolve mais). */
function showComposerToast(msg) {
    const wrap = document.querySelector('.composer-input-wrap');
    if (!wrap)
        return;
    let toast = wrap.querySelector('.toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.className = 'toast';
        wrap.appendChild(toast);
    }
    toast.textContent = msg;
    if (toastTimer)
        window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast === null || toast === void 0 ? void 0 : toast.remove(), 4500);
}
/** Navega/seleciona no Word o tópico cujo título casa com `label`. Resolve por
 *  TEXTO contra o outline vivo — imune a deslocamento de índice. */
async function gotoHeading(label) {
    const target = normLabel(label);
    if (!target)
        return;
    const outline = await fetchOutline();
    // 1) casamento exato normalizado; 2) fallback: título que COMEÇA com o rótulo
    //    (cobre rótulo abreviado, ex.: só a numeração "2.1").
    let hit = outline.find((h) => normLabel(h.text) === target);
    if (!hit)
        hit = outline.find((h) => normLabel(h.text).startsWith(target));
    if (!hit) {
        showComposerToast('Esse trecho pode ter mudado desde a resposta — não encontrei o tópico no documento atual.');
        return;
    }
    try {
        await Word.run(async (context) => {
            const paras = context.document.body.paragraphs;
            paras.load('items');
            await context.sync();
            const p = paras.items[hit.index];
            if (!p)
                throw new Error('índice fora do intervalo');
            // No Word, selecionar o parágrafo já rola a tela até ele (navegação +
            // destaque numa tacada). Paragraph.select existe desde o WordApi 1.1.
            p.select();
            await context.sync();
        });
    }
    catch (e) {
        dlog('[gotoHeading] falha ao selecionar', e);
        showComposerToast('Não consegui navegar até o tópico agora.');
    }
}
/** Delegação: clique/Enter/Espaço num chip de referência navega até o tópico. */
function wireReferenceLinks() {
    const handler = (e) => {
        var _a, _b;
        const a = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, `a[href="${REF_HREF}"]`);
        if (!a)
            return;
        e.preventDefault();
        void gotoHeading(a.textContent || '');
    };
    messagesEl.addEventListener('click', handler);
    messagesEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ')
            handler(e);
    });
}
const mentionState = {
    open: false,
    start: -1, // índice do '@' no textarea
    items: [],
    active: 0,
};
/** Menções escolhidas neste rascunho (consumidas ao enviar). */
const pendingMentions = [];
let mentionOutline = [];
let mentionHasSelection = false;
function mentionMenuEl() {
    return document.getElementById('mention-menu');
}
/** Fragmento digitado após o '@' até o caret (null = fechou: espaço/fora). */
function currentMentionQuery() {
    var _a;
    if (!mentionState.open)
        return null;
    const v = promptEl.value;
    const caret = (_a = promptEl.selectionStart) !== null && _a !== void 0 ? _a : v.length;
    if (mentionState.start < 0 || caret < mentionState.start + 1)
        return null;
    const frag = v.slice(mentionState.start + 1, caret);
    if (/\s/.test(frag))
        return null; // espaço encerra o filtro (tipo VSCode)
    return frag;
}
function closeMentionMenu() {
    mentionState.open = false;
    mentionState.start = -1;
    const el = mentionMenuEl();
    el.hidden = true;
    el.innerHTML = '';
}
async function openMentionMenu(atIndex) {
    mentionState.open = true;
    mentionState.start = atIndex;
    mentionState.active = 0;
    // Snapshot FRESCO: reflete o estado atual do documento (nada de cache velho).
    mentionOutline = await fetchOutline();
    const sel = await getSelection();
    mentionHasSelection = !!(sel && sel.text.trim());
    // O usuário pode ter fechado/movido enquanto o await corria.
    const q = currentMentionQuery();
    if (q === null) {
        closeMentionMenu();
        return;
    }
    renderMentionMenu(q);
}
function renderMentionMenu(query) {
    var _a;
    const q = normLabel(query);
    const items = [];
    if (mentionHasSelection && (!q || 'selecionado'.startsWith(q))) {
        items.push({ kind: 'selection', label: 'selecionado' });
    }
    for (const h of mentionOutline) {
        if (!q || normLabel(h.text).includes(q)) {
            items.push({ kind: 'heading', label: h.text, level: (_a = h.level) !== null && _a !== void 0 ? _a : 1 });
        }
    }
    mentionState.items = items;
    if (mentionState.active >= items.length)
        mentionState.active = Math.max(0, items.length - 1);
    const el = mentionMenuEl();
    if (!items.length) {
        el.innerHTML = `<div class="mention-empty">Nenhum tópico${query ? ` para “${escapeHtml(query)}”` : ' no documento'}.</div>`;
        el.hidden = false;
        return;
    }
    el.innerHTML = items
        .map((it, i) => {
        const active = i === mentionState.active ? ' is-active' : '';
        if (it.kind === 'selection') {
            return `<div class="mention-item mention-selection${active}" data-i="${i}" role="option">
          <span class="mention-num">@sel</span><span class="mention-text">texto selecionado</span></div>`;
        }
        const indent = 10 + Math.max(0, it.level - 1) * 10;
        return `<div class="mention-item${active}" data-i="${i}" role="option" style="padding-left:${indent}px">
        <span class="mention-text">${escapeHtml(it.label)}</span></div>`;
    })
        .join('');
    el.hidden = false;
}
/** Insere a menção escolhida no lugar do `@query`, registra em pendingMentions. */
function insertMention(i) {
    var _a;
    const it = mentionState.items[i];
    if (!it)
        return;
    const token = it.kind === 'selection' ? '@selecionado' : `@«${it.label}»`;
    const v = promptEl.value;
    const caret = (_a = promptEl.selectionStart) !== null && _a !== void 0 ? _a : v.length;
    const before = v.slice(0, mentionState.start);
    const after = v.slice(caret);
    const insert = `${token} `;
    promptEl.value = before + insert + after;
    const newCaret = (before + insert).length;
    promptEl.setSelectionRange(newCaret, newCaret);
    pendingMentions.push(it.kind === 'selection'
        ? { token, kind: 'selection' }
        : { token, kind: 'heading', headingText: it.label });
    closeMentionMenu();
    autoGrow();
    renderPromptHighlight();
    promptEl.focus();
}
/** Reconstrói o backdrop realçando os tokens de menção presentes no rascunho.
 *  O texto "normal" fica na cor de sempre; cada `@«...»`/`@selecionado` vira um
 *  <span.mention-hl> (roxo claro). Barato: roda a cada tecla do composer. */
function renderPromptHighlight() {
    const bd = document.getElementById('prompt-backdrop');
    if (!bd)
        return;
    const text = promptEl.value;
    let html = escapeHtml(text);
    // Só realça tokens REAIS escolhidos no menu e que ainda existam no texto.
    // Maior primeiro: evita um token ser substring de outro ao substituir.
    const tokens = Array.from(new Set(pendingMentions.map((m) => m.token)))
        .filter((t) => text.includes(t))
        .sort((a, b) => b.length - a.length);
    for (const t of tokens) {
        const esc = escapeHtml(t);
        html = html.split(esc).join(`<span class="mention-hl">${esc}</span>`);
    }
    // pre-wrap engole a última linha quando o texto termina em '\n': um espaço
    // preserva a altura, mantendo o alinhamento com a textarea.
    if (text.endsWith('\n'))
        html += ' ';
    bd.innerHTML = html;
    bd.scrollTop = promptEl.scrollTop;
}
/** Reage ao texto digitado: abre o menu num `@` de fronteira ou refiltra. */
function updateMentionOnInput() {
    var _a;
    if (mentionState.open) {
        const q = currentMentionQuery();
        if (q === null) {
            closeMentionMenu();
            return;
        }
        renderMentionMenu(q);
        return;
    }
    const v = promptEl.value;
    const caret = (_a = promptEl.selectionStart) !== null && _a !== void 0 ? _a : v.length;
    const prev = caret > 0 ? v[caret - 1] : '';
    const before = caret > 1 ? v[caret - 2] : '';
    // Abre quando o '@' está em fronteira de palavra (início ou após espaço/abre-aspas).
    if (prev === '@' && (caret === 1 || /[\s(«"']/.test(before))) {
        void openMentionMenu(caret - 1);
    }
}
/** Navegação por teclado no menu. Retorna true se consumiu a tecla. */
function handleMentionKeydown(e) {
    var _a, _b;
    if (!mentionState.open || !mentionState.items.length)
        return false;
    const n = mentionState.items.length;
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        mentionState.active = (mentionState.active + 1) % n;
        renderMentionMenu((_a = currentMentionQuery()) !== null && _a !== void 0 ? _a : '');
        return true;
    }
    if (e.key === 'ArrowUp') {
        e.preventDefault();
        mentionState.active = (mentionState.active - 1 + n) % n;
        renderMentionMenu((_b = currentMentionQuery()) !== null && _b !== void 0 ? _b : '');
        return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionState.active);
        return true;
    }
    if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionMenu();
        return true;
    }
    return false;
}
function wireMentions() {
    // mousedown (não click): não perde o foco do textarea antes de inserir.
    mentionMenuEl().addEventListener('mousedown', (e) => {
        var _a, _b;
        const item = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, '.mention-item');
        if (!item)
            return;
        e.preventDefault();
        insertMention(Number(item.getAttribute('data-i')));
    });
    // Clique fora fecha o menu.
    document.addEventListener('click', (e) => {
        if (!mentionState.open)
            return;
        const t = e.target;
        if (t.closest('#mention-menu') || t === promptEl)
            return;
        closeMentionMenu();
    });
}
/** Lines do bloco "Menções" do turno (consumidas por buildUserTurnText). */
let activeMentionLines = [];
/** Ao enviar: transforma cada menção ainda presente no texto num mapeamento
 *  preciso para o modelo (âncora exata), reaproveitando a máquina de anchorText. */
function captureMentionContext(prompt) {
    const lines = [];
    const seen = new Set();
    for (const m of pendingMentions) {
        if (seen.has(m.token) || !prompt.includes(m.token))
            continue; // token apagado pelo usuário
        seen.add(m.token);
        if (m.kind === 'selection') {
            lines.push('- `@selecionado` = o TEXTO SELECIONADO pelo usuário (ver "Seleção atual" acima). Aja sobre esse trecho.');
        }
        else {
            lines.push(`- \`${m.token}\` = o tópico cujo título é EXATAMENTE «${m.headingText}». ` +
                'Use esse título como anchorText para localizar/editar essa seção; não confunda com seções de nome parecido.');
        }
    }
    activeMentionLines = lines;
}
// --- Anexos (RF11) ---------------------------------------------------------
// Estado dos anexos pendentes (valem para o PRÓXIMO envio). Chips acima do
// composer; consumidos e limpos ao enviar. Validação de limites em
// util/attachments.ts (3 arquivos, 30MB total, tetos por tipo).
let pendingAttachments = [];
/**
 * Anexos da CONVERSA: tudo o que o usuário já enviou e que continua valendo
 * nos pedidos seguintes (ver `util/attachmentContext.ts`). `pendingAttachments`
 * são os do rascunho, ainda não enviados; ao enviar, entram aqui.
 */
let conversationAttachments = [];
/** Rótulo curto do chip (IMG/PDF/TXT) — usado no composer e na bolha enviada. */
function attachmentKindLabel(kind) {
    return kind === 'image' ? 'IMG' : kind === 'pdf' ? 'PDF' : 'TXT';
}
function renderAttachmentChips() {
    const host = document.getElementById('attach-chips');
    if (!host)
        return;
    host.hidden = pendingAttachments.length === 0;
    host.innerHTML = '';
    for (const a of pendingAttachments) {
        const chip = document.createElement('span');
        chip.className =
            'attach-chip' +
                (a.status === 'processing' ? ' is-processing' : a.status === 'error' ? ' is-error' : '');
        const kind = document.createElement('span');
        kind.className = 'chip-kind';
        kind.textContent = attachmentKindLabel(a.kind);
        const name = document.createElement('span');
        name.className = 'chip-name';
        name.textContent = a.name;
        name.title = a.status === 'error' ? a.error || a.name : a.name;
        const size = document.createElement('span');
        size.className = 'chip-size';
        size.textContent =
            a.status === 'processing' ? 'processando…' : a.status === 'error' ? 'erro' : formatBytes(a.size);
        const remove = document.createElement('button');
        remove.className = 'chip-remove';
        remove.textContent = '×';
        remove.title = 'Remover anexo';
        remove.onclick = () => {
            pendingAttachments = pendingAttachments.filter((x) => x.id !== a.id);
            renderAttachmentChips();
        };
        chip.append(kind, name, size, remove);
        host.appendChild(chip);
    }
}
/**
 * Chips dos anexos que continuam valendo na conversa. Existem para o usuário
 * ver o que o copiloto ainda está lendo — e poder tirar: cada arquivo vai em
 * todo pedido, então um PDF esquecido ali é custo em todo turno.
 */
function renderConversationAttachments() {
    const host = document.getElementById('context-chips');
    if (!host)
        return;
    host.hidden = conversationAttachments.length === 0;
    host.innerHTML = '';
    if (!conversationAttachments.length)
        return;
    const label = document.createElement('span');
    label.className = 'context-label';
    label.textContent = 'Na conversa';
    host.appendChild(label);
    for (const a of conversationAttachments) {
        const chip = document.createElement('span');
        chip.className = 'attach-chip';
        const kind = document.createElement('span');
        kind.className = 'chip-kind';
        kind.textContent = attachmentKindLabel(a.kind);
        const name = document.createElement('span');
        name.className = 'chip-name';
        name.textContent = a.name;
        name.title = a.name;
        const remove = document.createElement('button');
        remove.className = 'chip-remove';
        remove.textContent = '×';
        remove.title = 'Tirar da conversa';
        remove.setAttribute('aria-label', `Tirar ${a.name} da conversa`);
        remove.onclick = () => {
            conversationAttachments = conversationAttachments.filter((x) => x.id !== a.id);
            renderConversationAttachments();
            logStandard({ type: 'INFO', event: 'attachment.removed_from_conversation', detail: { kind: a.kind } });
        };
        chip.append(kind, name, remove);
        host.appendChild(chip);
    }
}
/** Recebe arquivos (clipe ou drag&drop), valida limites e processa cada um. */
async function addAttachmentFiles(files) {
    if (!files.length)
        return;
    if (!documentType) {
        showComposerToast('Escolha primeiro o tipo de documento para anexar arquivos.');
        return;
    }
    const batchError = validateBatch(pendingAttachments, files);
    if (batchError) {
        showComposerToast(batchError);
        return;
    }
    for (const file of files) {
        // Chip "processando" imediato; o processamento troca pelo estado final.
        const placeholder = {
            id: `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
            name: file.name,
            size: file.size,
            kind: 'text',
            mediaType: '',
            status: 'processing',
        };
        pendingAttachments.push(placeholder);
        renderAttachmentChips();
        const done = await processFile(file);
        pendingAttachments = pendingAttachments.map((x) => (x.id === placeholder.id ? done : x));
        renderAttachmentChips();
        if (done.status === 'error' && done.error)
            showComposerToast(done.error);
        // Telemetria: só tipo/tamanho/desfecho — nunca nome nem conteúdo.
        logStandard({
            type: done.status === 'error' ? 'WARNING' : 'INFO',
            event: 'attachment.add',
            detail: { kind: done.kind, size: done.size, status: done.status },
        });
    }
}
function wireAttachments() {
    const btn = document.getElementById('attach-btn');
    const input = document.getElementById('attach-input');
    const overlay = document.getElementById('drop-overlay');
    if (!btn || !input || !overlay)
        return;
    input.setAttribute('accept', ACCEPT_ATTR);
    btn.onclick = () => input.click();
    input.onchange = () => {
        var _a;
        void addAttachmentFiles(Array.from((_a = input.files) !== null && _a !== void 0 ? _a : []));
        input.value = ''; // permite reanexar o mesmo arquivo depois
    };
    // Drag & drop — SÓ na tela de chat (anexo acompanha o prompt; nas telas
    // anteriores não faz sentido e o drop é ignorado).
    //
    // Visibilidade do overlay SEM contador dragenter/dragleave: em webviews o
    // dragleave nem sempre dispara (arrasto cancelado, saiu da janela) e o
    // overlay ficava PRESO na tela (bug real). Em vez disso, o fluxo contínuo de
    // `dragover` (dispara a cada ~100-300ms enquanto o arquivo está sobre o
    // painel) funciona como heartbeat: cada evento mostra o overlay e rearma um
    // timer curto; sem eventos, o timer esconde sozinho — autocorretivo.
    let dragHideTimer;
    const hideOverlay = () => {
        if (dragHideTimer)
            window.clearTimeout(dragHideTimer);
        dragHideTimer = undefined;
        overlay.hidden = true;
    };
    const hasFiles = (e) => { var _a, _b; return Array.from((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.types) !== null && _b !== void 0 ? _b : []).includes('Files'); };
    const inChat = () => !!documentType;
    document.addEventListener('dragover', (e) => {
        if (!hasFiles(e))
            return;
        // preventDefault SEMPRE (mesmo fora do chat): sem isto o webview abre o
        // arquivo solto, navegando para fora do painel.
        e.preventDefault();
        if (!inChat())
            return;
        overlay.hidden = false;
        if (dragHideTimer)
            window.clearTimeout(dragHideTimer);
        dragHideTimer = window.setTimeout(hideOverlay, 300);
    });
    document.addEventListener('drop', (e) => {
        var _a, _b;
        if (!hasFiles(e))
            return;
        e.preventDefault();
        hideOverlay();
        if (!inChat())
            return; // fora do chat: ignora o drop silenciosamente
        void addAttachmentFiles(Array.from((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) !== null && _b !== void 0 ? _b : []));
    });
    document.addEventListener('dragend', hideOverlay);
}
function wireComposer() {
    dlog('[wireComposer] vinculando eventos...');
    // Bind onclick — mesmo botão alterna enviar/parar (ver setBusy).
    sendBtn.onclick = () => {
        if (sendBtn.classList.contains('busy')) {
            dlog('[wireComposer.onclick] botão clicado (parar)');
            currentAbort === null || currentAbort === void 0 ? void 0 : currentAbort.abort();
        }
        else {
            dlog('[wireComposer.onclick] botão clicado (enviar)');
            send();
        }
    };
    dlog('[wireComposer] onclick vinculado');
    // Bind input
    promptEl.addEventListener('input', () => {
        dlog('[wireComposer.oninput] input disparado');
        autoGrow();
        updateMentionOnInput();
        renderPromptHighlight();
    });
    // Backdrop de realce acompanha o scroll interno da textarea (texto > 140px).
    promptEl.addEventListener('scroll', () => {
        const bd = document.getElementById('prompt-backdrop');
        if (bd)
            bd.scrollTop = promptEl.scrollTop;
    });
    dlog('[wireComposer] input listener adicionado');
    // Bind keydown
    promptEl.addEventListener('keydown', (e) => {
        dlog('[wireComposer.onkeydown] tecla pressionada:', e.key);
        // Menu de menções aberto tem prioridade sobre o Enter=enviar.
        if (handleMentionKeydown(e))
            return;
        if (e.key === 'Enter' && !e.shiftKey) {
            dlog('[wireComposer.onkeydown] Enter detectado (sem Shift)');
            e.preventDefault();
            send();
        }
    });
    dlog('[wireComposer] keydown listener adicionado');
    // Referências clicáveis (RF04) + menções @ — ambas apoiadas no outline.
    wireReferenceLinks();
    wireMentions();
    // Anexos (RF11): clipe + drag&drop.
    wireAttachments();
    dlog('[wireComposer] ✅ eventos vinculados com sucesso');
}
async function send() {
    var _a;
    // Com uma pergunta aberta, a resposta é no painel de esclarecimento: o campo
    // normal está escondido, mas o atalho de teclado ainda alcançaria esta função.
    if (pendingAsk)
        return;
    const text = promptEl.value.trim();
    if (!text)
        return;
    // Guard: sem tipo de documento escolhido, não fala com o modelo (a UI já
    // bloqueia o composer, mas garantimos aqui também).
    if (!documentType)
        return;
    // Anexos: bloqueia envio com anexo ainda processando ou com erro (o usuário
    // remove o chip com erro ou espera terminar) — nunca envia estado ambíguo.
    if (pendingAttachments.some((a) => a.status === 'processing')) {
        showComposerToast('Aguarde os anexos terminarem de processar.');
        return;
    }
    if (pendingAttachments.some((a) => a.status === 'error')) {
        showComposerToast('Remova o anexo com erro (×) antes de enviar.');
        return;
    }
    dlog('[send] starting with text:', text);
    removeOriginalRestore();
    formatReview = null;
    // Resolve as menções @ ainda presentes no texto em âncoras precisas para o
    // turno, ANTES de limpar o rascunho.
    captureMentionContext(text);
    closeMentionMenu();
    // Anexos deste envio: consumidos pelo turno e limpos do rascunho.
    const turnAttachments = pendingAttachments.filter((a) => a.status === 'ready');
    // Guarda os BYTES das imagens deste turno: os anexos são consumidos aqui para
    // virar blocos do modelo, e sem isso `insert_image` não teria o que inserir.
    rememberInsertableImages(turnAttachments);
    pendingAttachments = [];
    renderAttachmentChips();
    const userMsgEl = addMessage('user', text);
    if (turnAttachments.length) {
        // Mostra os anexos junto da mensagem no chat (só nomes — visual).
        const note = document.createElement('div');
        note.className = 'attach-chips';
        note.style.marginTop = '6px';
        for (const a of turnAttachments) {
            const chip = document.createElement('span');
            chip.className = 'attach-chip';
            chip.innerHTML = `<span class="chip-kind">${attachmentKindLabel(a.kind)}</span>`;
            const nm = document.createElement('span');
            nm.className = 'chip-name';
            nm.textContent = a.name;
            chip.appendChild(nm);
            note.appendChild(chip);
        }
        (_a = userMsgEl.querySelector('.bubble')) === null || _a === void 0 ? void 0 : _a.appendChild(note);
    }
    promptEl.value = '';
    pendingMentions.length = 0;
    autoGrow();
    renderPromptHighlight(); // limpa o backdrop junto com o rascunho
    setBusy(true);
    try {
        await runAgentLoop(text, turnAttachments, userMsgEl);
    }
    finally {
        setBusy(false);
        currentAbort = null;
    }
}
/**
 * Executa o agent loop:
 * 1. Coleta contexto do documento
 * 2. Manda pro backend
 * 3. Se tool_use, executa ferramentas aqui
 * 4. Reenvia com tool_result se necessário
 * 5. Repete até stop_reason != "tool_use"
 */
async function runAgentLoop(initialPrompt, attachments = [], 
/**
 * Mensagem do usuário que abriu esta corrente. É onde um eventual pedido de
 * esclarecimento fica dobrado — passe adiante nos turnos de resposta para que
 * a segunda pergunta caia no MESMO log, e não num novo.
 */
chainAnchor = null, 
/** O usuário acabou de confirmar conteúdo sem relação com o documento. */
opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    setStatus('lendo o documento');
    // Começo de turno: remove o botão "Desfazer" do turno anterior (as
    // alterações anteriores ficam "aceitas"). Um novo snapshot só é tirado se
    // este turno for, de fato, modificar o documento.
    removeUndo();
    removeContinue();
    // O pedido fica marcado "em andamento" até o fim do turno e é gravado já (ver
    // beginTurnPersistence). A bolha é a mensagem do usuário que abriu o turno; na
    // formatação automática, que não tem mensagem, é a saudação que a anuncia.
    beginTurnPersistence(chainAnchor ? Number(chainAnchor.dataset.transcriptIndex) : transcript.length - 1);
    let preWriteSnapshot = null;
    let didWrite = false;
    // Escritas que deram certo neste turno: é o que JÁ está no documento se o
    // turno parar antes do fim (ver util/turnRecovery.ts).
    const writes = [];
    // O modelo foi avisado de que os passos estavam acabando? Então a resposta
    // final, mesmo encerrando o turno sozinho, costuma deixar trabalho pendente —
    // e ganha o botão Continuar.
    let wrapUpSent = false;
    // Texto do turno (com contexto), para o histórico de um turno interrompido.
    let turnText = '';
    /**
     * Turno que não terminou: o histórico ganha o pedido e uma nota do que houve
     * — o que mudou no documento e como seguir —, sem os blocos de ferramenta.
     * Antes ele era jogado fora, e um "continue" seguinte não sabia o que já
     * estava feito.
     */
    const recordInterruptedTurn = (reason, detail = '') => {
        if (!turnText)
            return; // parou antes de montar o pedido: nada a registrar
        conversationHistory = trimHistory(stripContextBlocks(appendInterruptedTurn(conversationHistory, turnText, interruptedTurnNote(reason, writes, detail))));
    };
    // Guarda anti-loop: conta chamadas IDÊNTICAS (nome+input) que falharam. Se a
    // mesma ação errar repetidamente, o modelo está "preso" — abortamos com uma
    // mensagem útil em vez de gastar todos os passos.
    const erroredCalls = new Map();
    // Controle de cancelamento (botão "Parar") e acumulador de tokens do turno.
    currentAbort = new AbortController();
    const signal = currentAbort.signal;
    const turnTokens = { input: 0, output: 0, cacheRead: 0 };
    // ---- Telemetria do TURNO (1 llm_interaction_log por envio do usuário) ----
    const turnId = newTurnId();
    const turnStart = Date.now();
    const tierTokens = emptyTurnTokens();
    let stepCount = 0;
    let finalResponseText = '';
    let turnPlanSeen = false; // planner rodou? (+1 chamada real ao Bedrock)
    let turnUsdCost = 0;
    let turnClosed = false;
    const endTurn = (status, docType) => {
        var _a;
        if (turnClosed)
            return; // garante exatamente UM registro por turno
        turnClosed = true;
        lastTurnStatus = status;
        // Falha vira nota no contexto do PRÓXIMO turno — o planejador lê o mesmo
        // texto. Cada desfecho pede uma conduta: limite de passos é CONTINUAR em
        // partes (o caminho estava certo, só era longo); loop é mudar de caminho.
        const NOTA_DA_FALHA = {
            LOOP_GUARD: 'parou porque a mesma ação falhou repetidas vezes (LOOP_GUARD). Escolha um caminho ' +
                'diferente do que falhou.',
            MAX_STEPS: 'parou no limite de passos antes de terminar (MAX_STEPS); o que já foi feito e o que ' +
                'faltou está no histórico. Se o usuário pedir para continuar, siga do ponto em que ' +
                'parou, uma parte por vez, sem refazer o que já está no documento.',
            ERROR: 'parou por um erro de execução (ERROR). Antes de refazer algo, confira no documento o ' +
                'que já pode ter sido feito.',
            // O próximo turno precisa saber disto para NÃO repetir a leitura em massa
            // que encheu o contexto — trabalhe por janelas menores.
            CONTEXT_LIMIT: 'encheu o contexto antes de terminar (CONTEXT_LIMIT). Leia em janelas menores e trate ' +
                'uma seção por vez.',
        };
        lastTurnFailure = (_a = NOTA_DA_FALHA[status]) !== null && _a !== void 0 ? _a : lastTurnFailure;
        logLlmInteraction({
            turn_id: turnId,
            status,
            duration: Date.now() - turnStart,
            document_type: docType !== null && docType !== void 0 ? docType : undefined,
            prompt: initialPrompt,
            response: finalResponseText,
            step_count: stepCount,
            // Cada /agent/step = 1 chamada de execução; o planner (1º passo, quando
            // devolve plano) soma +1. Aproximação: planejamento que falhou não conta.
            llm_call_count: stepCount + (turnPlanSeen ? 1 : 0),
            model_simple_input_tokens: tierTokens.simple.input,
            model_simple_output_tokens: tierTokens.simple.output,
            model_default_input_tokens: tierTokens.standard.input,
            model_default_output_tokens: tierTokens.standard.output,
            model_complex_input_tokens: tierTokens.complex.input,
            model_complex_output_tokens: tierTokens.complex.output,
            usd_cost: Number(turnUsdCost.toFixed(8)),
        });
    };
    try {
        // O template do tipo escolhido já foi aplicado ao selecionar (selectTemplate).
        // Coleta contexto
        dlog('[runAgentLoop] coletando metadados...');
        const metadata = await getDocumentMetadata();
        dlog('[runAgentLoop] metadados coletados:', metadata);
        const selection = await getSelection();
        dlog('[runAgentLoop] seleção lida:', selection);
        // Outline no contexto do turno (percepção): o modelo — e o planejador, que
        // lê o mesmo texto — começa vendo os títulos reais com índice. Best-effort:
        // sem outline o turno segue como antes.
        let overview = null;
        try {
            overview = (await getDocumentOverview());
        }
        catch (e) {
            dlog('[runAgentLoop] overview indisponível (segue sem outline):', e);
        }
        setStatus('pensando');
        // Anexa este turno à MEMÓRIA da conversa (acumulada entre mensagens, não
        // só dentro deste turno). Sempre mandamos `messages` — nunca `prompt`
        // solto — para o Claude ver os turnos anteriores. `selection`/
        // `document_metadata` também vão no request por transparência/log, mas
        // quem importa pro modelo é o texto já embutido na mensagem.
        // Anexos deste envio entram nos da conversa. O texto do turno só ganha uma
        // nota dizendo QUAIS chegaram agora: o conteúdo vai no bloco fixo do início.
        if (attachments.length) {
            const juncao = mergeConversationAttachments(conversationAttachments, attachments);
            conversationAttachments = juncao.library;
            renderConversationAttachments();
            if (juncao.dropped.length) {
                showComposerToast(`Para caber no limite da conversa, saíram os anexos mais antigos: ${juncao.dropped.join(', ')}.`);
            }
        }
        // Edição manual desde a última resposta: o modelo é avisado no contexto, e o
        // usuário vê, discretamente, que ela foi levada em conta — o painel antes
        // percebia e não dizia nada.
        if (chainAnchor && !documentRevertedNote && documentDrifted(metadata)) {
            addTurnNote(chainAnchor, 'Considerei as alterações feitas direto no documento.');
        }
        turnText = buildUserTurnText(initialPrompt + newAttachmentsNote(attachments), selection, metadata, overview);
        lastTurnFailure = null; // notas valem só para o 1º turno seguinte
        resumeNote = null;
        documentRevertedNote = false;
        activeMentionLines = []; // menções já embutidas no turnText; não vazam p/ o próximo
        // O turno é SEMPRE texto (preserva os invariantes de trimHistory e
        // stripContextBlocks). Os arquivos da conversa abrem `messages` num par fixo,
        // refeito a cada pedido e retirado antes de gravar o histórico — é o que os
        // mantém disponíveis nos turnos seguintes. O cache é decidido no backend.
        const startingMessages = [
            ...buildAttachmentContext(conversationAttachments),
            ...conversationHistory,
            { role: 'user', content: turnText },
        ];
        let request = Object.assign({ messages: startingMessages, selection: selection || undefined, document_metadata: metadata, document_type: documentType || undefined, rules: selectedRules || undefined, session_id: sessionId, turn_id: turnId }, (opts.scopeConfirmed ? { scope_confirmed: true } : {}));
        // Modelo/plano do turno: vazios no 1º passo (o backend planeja e escolhe o
        // modelo); depois ecoamos o que ele devolveu para o turno inteiro usar o
        // MESMO modelo/plano (backend é stateless). `turnTier` é só para exibição.
        let turnModel;
        let turnPlan;
        let turnTier;
        dlog('[runAgentLoop] enviando request:', {
            prompt: initialPrompt,
            has_selection: !!selection,
            metadata_words: metadata.word_count,
            history_messages: conversationHistory.length,
        });
        // Loop: enquanto houver tool_use. O teto é de PASSOS (idas e vindas ao
        // modelo), não de tempo — não existe timeout de turno. Vem de `LIMIT_STEPS`
        // no .env, injetado em build-time (ver webpack.config.js / env.d.ts).
        let maxSteps = __LIMIT_STEPS__;
        for (let step = 0; step < maxSteps; step++) {
            dlog(`[runAgentLoop] passo ${step}`);
            const typing = showTyping();
            try {
                dlog('[runAgentLoop] chamando agentStep...');
                // A repetição é invisível para o usuário, mas não para quem opera:
                // repetição frequente = pods saindo no meio de turnos (rollout, HPA).
                const response = await agentStep(request, signal, ({ attempt, status }) => logStandard({
                    type: 'WARNING',
                    event: 'agent.step_retried',
                    turn_id: turnId,
                    detail: { step, attempt, status },
                }));
                dlog('[runAgentLoop] resposta recebida:', {
                    stop_reason: response.stop_reason,
                    content_blocks: response.content.length,
                    usage: response.usage,
                });
                typing.remove();
                // Captura o modelo/plano escolhidos no 1º passo para reenviar nos demais.
                if (response.model)
                    turnModel = response.model;
                if (response.plan != null)
                    turnPlan = response.plan;
                if (response.tier)
                    turnTier = response.tier;
                if (response.plan)
                    turnPlanSeen = true;
                if (response.usd_cost)
                    turnUsdCost += response.usd_cost;
                if (response.plan_usage)
                    accumulateTierTokens(tierTokens, 'simple', response.plan_usage);
                // Telemetria do turno: conta o step e acumula tokens no tier certo.
                stepCount++;
                accumulateTierTokens(tierTokens, response.tier, response.usage);
                // Acumula tokens do turno (cada passo é uma chamada ao Claude).
                if (response.usage) {
                    turnTokens.input += (_a = response.usage.input_tokens) !== null && _a !== void 0 ? _a : 0;
                    turnTokens.output += (_b = response.usage.output_tokens) !== null && _b !== void 0 ? _b : 0;
                    turnTokens.cacheRead += (_c = response.usage.cache_read_input_tokens) !== null && _c !== void 0 ? _c : 0;
                }
                // Só mostramos a MENSAGEM FINAL (quando não há mais tool_use). A
                // narração intermediária ("Agora vou formatar...") é suprimida para o
                // chat ficar limpo: uma resposta curta no fim, e não um passo a passo.
                if (response.stop_reason !== 'tool_use') {
                    dlog('[runAgentLoop] turno concluído (stop_reason:', response.stop_reason, ')');
                    let lastAssistantEl = null;
                    const finalParts = [];
                    for (const block of response.content) {
                        if (block.type === 'text' && block.text)
                            finalParts.push(block.text);
                    }
                    finalResponseText = finalParts.join('\n\n');
                    // Pedido de esclarecimento? Então a resposta NÃO vira bolha: a
                    // pergunta vai para o log dobrável da mensagem original e o composer
                    // vira campo de resposta. Sem âncora (caminho inesperado) caímos no
                    // fluxo normal — melhor uma bolha estranha do que uma pergunta
                    // invisível. `stripAskBlock` cobre o caso de o modelo escrever prosa
                    // junto do bloco, contrariando a instrução.
                    const ask = parseAskBlock(finalResponseText);
                    if (ask && chainAnchor) {
                        const aside = stripAskBlock(finalResponseText);
                        if (aside)
                            appendClarifyEntry(chainAnchor, 'assistant', aside);
                        openAskPanel(ask, chainAnchor);
                        // Rodadas de `clarify` no mesmo pedido. O prompt manda decidir TUDO
                        // numa rodada (até 3 perguntas, mostradas uma a uma); outra rodada
                        // só se uma resposta criou dúvida nova. Mais de uma é sinal de que o
                        // modelo perguntou antes de se contextualizar.
                        const rodada = ask.kind === 'clarify' ? Number(chainAnchor.dataset.clarifyRounds || '0') + 1 : 0;
                        if (ask.kind === 'clarify')
                            chainAnchor.dataset.clarifyRounds = String(rodada);
                        logStandard({
                            type: rodada > 1 ? 'WARNING' : 'INFO',
                            event: 'clarify.asked',
                            turn_id: turnId,
                            detail: { kind: ask.kind, round: rodada, questions: ask.questions.length },
                        });
                    }
                    else if (ask) {
                        // Turno sem mensagem de usuário na tela (formatação automática):
                        // não há onde dobrar a pergunta. Degrada para uma pergunta comum no
                        // chat — o usuário responde no campo normal. O que não pode
                        // acontecer é o bloco cru vazar como um trecho de código.
                        lastAssistantEl = addMessage('assistant', ask.questions.join('\n\n'));
                    }
                    else {
                        for (const part of finalParts)
                            lastAssistantEl = addMessage('assistant', part);
                    }
                    endTurn('SUCCESS', documentType);
                    setStatus(formatTokenStatus(turnTokens, turnTier));
                    // Turno concluído com sucesso: agora sim persiste a memória — este é
                    // o único ponto em que o histórico sempre termina numa mensagem
                    // `assistant` válida (ver comentário em conversationHistory). Guarda
                    // sem os blocos de contexto (só prompts), com leituras/escritas
                    // volumosas podadas (pruneBulkyToolBlocks) e SEM o par fixo de anexos
                    // da conversa (stripAttachmentContext — ele é refeito a cada pedido,
                    // e gravá-lo duplicaria os arquivos e levaria base64 para o banco);
                    // o contexto fresco entra no próximo turno.
                    conversationHistory = trimHistory(stripContextBlocks(pruneBulkyToolBlocks(stripAttachmentContext(response.messages))));
                    // Houve escrita neste turno: ancora o "Desfazer" ABAIXO desta resposta
                    // (restaura o snapshot tirado antes da 1ª alteração deste turno).
                    if (didWrite && preWriteSnapshot) {
                        undoSnapshot = preWriteSnapshot;
                        // Turno que escreveu E terminou perguntando não tem bolha de
                        // resposta: ancora o "Desfazer" na mensagem original.
                        attachUndo(lastAssistantEl !== null && lastAssistantEl !== void 0 ? lastAssistantEl : chainAnchor);
                    }
                    // Fechou por causa do aviso de fim de passos: a resposta diz o que
                    // falta, e continuar fica a um toque (como no limite estourado).
                    if (wrapUpSent && !ask && lastAssistantEl)
                        attachContinue(lastAssistantEl);
                    return;
                }
                // Há tool_use: executa as ferramentas (sem poluir o chat com texto).
                dlog('[runAgentLoop] executando ferramentas...');
                setStatus('escrevendo no documento');
                const toolResults = [];
                // Dedupe DENTRO do passo: o modelo ocasionalmente emite a MESMA chamada
                // duas vezes no mesmo bloco de resposta — executar as duas duplicaria a
                // edição no documento. A segunda recebe um tool_result informativo (a
                // API exige um result para CADA tool_use) sem tocar no documento.
                const stepSigs = new Set();
                for (const block of response.content) {
                    if (block.type === 'tool_use') {
                        const isWrite = WRITE_TOOLS.has(block.name || '');
                        const sig = `${block.name}:${JSON.stringify((_d = block.input) !== null && _d !== void 0 ? _d : {})}`;
                        if (stepSigs.has(sig)) {
                            dlog(`[runAgentLoop] tool duplicada no mesmo passo, ignorada: ${block.name}`);
                            toolResults.push({
                                type: 'tool_result',
                                tool_use_id: block.id,
                                content: 'Chamada idêntica duplicada neste passo — ignorada (a primeira já foi executada; não repita).',
                                is_error: false,
                            });
                            continue;
                        }
                        stepSigs.add(sig);
                        // Tira o snapshot ANTES da primeira escrita do turno (estado a que
                        // o "Desfazer" volta). Só uma vez por turno.
                        if (isWrite && preWriteSnapshot === null) {
                            preWriteSnapshot = await snapshotDocument();
                        }
                        try {
                            dlog(`[runAgentLoop] executando tool: ${block.name}`);
                            const result = await executeTool(block.name || '', block.input || {});
                            dlog(`[runAgentLoop] resultado da tool:`, { success: !result.is_error });
                            if (result.is_error)
                                erroredCalls.set(sig, ((_e = erroredCalls.get(sig)) !== null && _e !== void 0 ? _e : 0) + 1);
                            else if (isWrite) {
                                didWrite = true;
                                writes.push(recordFromToolResult(block.name || '', result.content));
                            }
                            // Telemetria: só o NOME da ferramenta e o desfecho (inputs podem
                            // conter texto do documento — nunca vão em standard_logs). A
                            // mensagem de erro cita o trecho procurado (`Texto não encontrado:
                            // "<trecho>"`), que é texto do documento: sai redigida.
                            logStandard({
                                type: result.is_error ? 'ERROR' : 'SUCCESS',
                                event: 'tool.execute',
                                component: 'office_tool',
                                turn_id: turnId,
                                message: result.is_error ? redactToolError(String(result.content)) : '',
                                detail: { tool: block.name, is_write: isWrite, step },
                            });
                            toolResults.push({
                                type: 'tool_result',
                                tool_use_id: block.id,
                                content: result.content,
                                is_error: result.is_error || false,
                            });
                        }
                        catch (e) {
                            console.error(`[runAgentLoop] erro ao executar tool ${block.name}:`, e);
                            erroredCalls.set(sig, ((_f = erroredCalls.get(sig)) !== null && _f !== void 0 ? _f : 0) + 1);
                            logStandard({
                                type: 'ERROR',
                                event: 'tool.execute',
                                component: 'office_tool',
                                turn_id: turnId,
                                message: redactToolError(String(e)),
                                detail: { tool: block.name, step },
                            });
                            toolResults.push({
                                type: 'tool_result',
                                tool_use_id: block.id,
                                content: `Erro: ${String(e)}`,
                                is_error: true,
                            });
                        }
                    }
                }
                // Anti-loop: se a MESMA chamada falhou 3+ vezes, o modelo está preso —
                // aborta com mensagem útil em vez de repetir até estourar os passos.
                if ([...erroredCalls.values()].some((c) => c >= 3)) {
                    console.warn('[runAgentLoop] abortando: ação repetidamente falha');
                    setStatus('');
                    const abortEl = addMessage('assistant', 'Travei repetindo a mesma ação. Me diga **onde** no documento (o título exato da seção) ou reformule o pedido.');
                    if (didWrite && preWriteSnapshot) {
                        undoSnapshot = preWriteSnapshot;
                        attachUndo(abortEl);
                    }
                    recordInterruptedTurn('loop_guard');
                    endTurn('LOOP_GUARD', documentType);
                    return;
                }
                // Próxima requisição: o array `messages` completo (devolvido pelo
                // backend, já com o turno do assistente) + os tool_result do usuário.
                // Ecoa model/plan para o backend não re-planejar (mesmo modelo no turno).
                let nextMessages = [
                    ...response.messages,
                    { role: 'user', content: toolResults },
                ];
                // Perto do teto, o modelo é AVISADO para fechar num ponto consistente —
                // antes ele era cortado no meio de uma operação, sem saber que o fim
                // estava chegando. O aviso vai junto dos resultados e fica no histórico.
                const restantes = maxSteps - (step + 1);
                if (restantes === wrapUpThreshold(maxSteps)) {
                    nextMessages[nextMessages.length - 1] = appendTextToToolResults(nextMessages[nextMessages.length - 1], wrapUpReminder(restantes));
                    wrapUpSent = true;
                }
                // ---- Orçamento de contexto ------------------------------------------
                // A base é o tamanho REAL do prompt que o provedor acabou de cobrar
                // (`usage`), não uma estimativa: anexo viaja como base64 e contar esses
                // caracteres como texto inflava a conta em até 26x — um PDF de 22
                // páginas matava o turno na primeira rodada. Sobre esse número real
                // soma-se só o DELTA que estamos acrescentando agora (tool_results, que
                // são texto e a estimativa por caracteres cobre bem).
                //
                // Sem `usage` não há base para julgar, e aí NÃO se bloqueia: um palpite
                // não pode cancelar o trabalho do usuário. Se o prompt de fato não
                // couber, o provedor recusa e o backend traduz isso num 413 acionável.
                const promptReal = promptTokensFromUsage(response.usage);
                let contextTokens = promptReal === null
                    ? null
                    : promptReal + estimateContextTokens([{ role: 'user', content: toolResults }]);
                if (contextTokens !== null && contextTokens > PRUNE_TRIGGER_TOKENS) {
                    const antes = contextTokens;
                    const cheio = estimateContextTokens(nextMessages);
                    nextMessages = pruneOlderTurnMessages(nextMessages);
                    // A poda mexe só em blocos de TEXTO, então o quanto ela economiza é
                    // exatamente o que a estimativa por caracteres sabe medir.
                    const economia = cheio - estimateContextTokens(nextMessages);
                    contextTokens = Math.max(0, antes - economia);
                    dlog('[runAgentLoop] poda de contexto no turno:', { antes, depois: contextTokens });
                    logStandard({
                        type: 'INFO',
                        event: 'context.pruned',
                        turn_id: turnId,
                        detail: { step, tokens_before: antes, tokens_after: contextTokens },
                    });
                }
                if (contextTokens !== null && contextTokens > PROMPT_BUDGET_TOKENS) {
                    // Nem podando cabe. Encerrar AQUI é melhor do que mandar e receber um
                    // erro de validação do provedor: o trabalho já feito continua no
                    // documento, o "Desfazer" continua disponível e o usuário recebe uma
                    // instrução acionável em vez de "erro ao processar requisição".
                    console.warn('[runAgentLoop] orçamento de contexto estourado:', contextTokens);
                    setStatus('');
                    // Só ações que existem no painel: o botão "Nova conversa" do header
                    // saiu em set/2026, e mandar o usuário procurá-lo o deixava sem saída.
                    // Anexo é o que mais pesa (vai inteiro em todo pedido), então, havendo
                    // algum, a saída mais eficaz é tirá-lo da conversa.
                    const ctxEl = addMessage('assistant', 'Este pedido acumulou mais conteúdo do que cabe em uma única conversa. ' +
                        'Parei aqui — o que já foi feito está no documento. ' +
                        'Continue pedindo por partes (ex.: uma seção por vez).' +
                        (conversationAttachments.length
                            ? ' Os anexos vão inteiros em todo pedido: tire da conversa os que não ' +
                                'forem mais necessários, no **×** ao lado do nome, em **Na conversa**.'
                            : ''));
                    if (didWrite && preWriteSnapshot) {
                        undoSnapshot = preWriteSnapshot;
                        attachUndo(ctxEl);
                    }
                    logStandard({
                        type: 'ERROR',
                        event: 'context.limit',
                        turn_id: turnId,
                        detail: { step, tokens: contextTokens, budget: PROMPT_BUDGET_TOKENS },
                    });
                    endTurn('CONTEXT_LIMIT', documentType);
                    return;
                }
                request = {
                    messages: nextMessages,
                    model: turnModel,
                    plan: turnPlan,
                    document_type: documentType || undefined,
                    rules: selectedRules || undefined,
                    session_id: sessionId,
                    turn_id: turnId,
                };
                setStatus('continuando');
            }
            catch (error) {
                typing.remove();
                // Cancelamento pelo usuário (botão "Parar") não é erro: encerra limpo.
                if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
                    dlog('[runAgentLoop] interrompido pelo usuário');
                    setStatus('');
                    const stoppedEl = addMessage('assistant', 'Interrompido. ⏹️');
                    if (didWrite && preWriteSnapshot) {
                        undoSnapshot = preWriteSnapshot;
                        attachUndo(stoppedEl);
                    }
                    logStandard({
                        type: 'WARNING',
                        event: 'turn.cancelled',
                        turn_id: turnId,
                        detail: { step, did_write: didWrite },
                    });
                    recordInterruptedTurn('cancelled');
                    endTurn('CANCELLED', documentType);
                    return;
                }
                const errorMsg = error instanceof Error ? error.message : String(error);
                console.error(`[runAgentLoop] erro no passo ${step}:`, error);
                const errorEl = addMessage('assistant', `Não consegui concluir: ${errorMsg}`);
                // Um erro no passo 20 não desfaz os 19 anteriores: o que já foi escrito
                // continua no documento, e o Desfazer tem que continuar ao alcance.
                if (didWrite && preWriteSnapshot) {
                    undoSnapshot = preWriteSnapshot;
                    attachUndo(errorEl);
                }
                setStatus('');
                logStandard({
                    type: 'ERROR',
                    event: 'agent_loop.step_error',
                    turn_id: turnId,
                    message: errorMsg.slice(0, 300),
                    detail: { step },
                });
                recordInterruptedTurn('error', errorMsg);
                endTurn('ERROR', documentType);
                return;
            }
        }
        // Esgotou os passos sem concluir. Antes de parar, UM último passo sem
        // ferramentas: o modelo diz o que já fez e o que falta — ele sabe; o painel
        // só sabe quais ferramentas rodaram. Esse passo também fecha o histórico
        // num estado válido (termina em texto do assistente), e é isso que faz o
        // "Continuar" seguinte saber onde parou.
        setStatus('resumindo');
        const typingFinal = showTyping();
        let resumo = '';
        try {
            const ultima = (_g = request.messages) === null || _g === void 0 ? void 0 : _g[request.messages.length - 1];
            if (ultima) {
                const fechamento = await agentStep(Object.assign(Object.assign({}, request), { messages: [
                        ...((_h = request.messages) !== null && _h !== void 0 ? _h : []).slice(0, -1),
                        appendTextToToolResults(ultima, FINALIZE_INSTRUCTION),
                    ], finalize: true }), signal);
                stepCount++;
                accumulateTierTokens(tierTokens, fechamento.tier, fechamento.usage);
                if (fechamento.usd_cost)
                    turnUsdCost += fechamento.usd_cost;
                if (fechamento.usage) {
                    turnTokens.input += (_j = fechamento.usage.input_tokens) !== null && _j !== void 0 ? _j : 0;
                    turnTokens.output += (_k = fechamento.usage.output_tokens) !== null && _k !== void 0 ? _k : 0;
                    turnTokens.cacheRead += (_l = fechamento.usage.cache_read_input_tokens) !== null && _l !== void 0 ? _l : 0;
                }
                const texto = fechamento.content
                    .filter((b) => b.type === 'text' && b.text)
                    .map((b) => b.text)
                    .join('\n\n')
                    .trim();
                if (fechamento.stop_reason !== 'tool_use' && texto) {
                    resumo = texto;
                    conversationHistory = trimHistory(stripContextBlocks(pruneBulkyToolBlocks(stripAttachmentContext(fechamento.messages))));
                }
            }
        }
        catch (e) {
            // Sem o resumo do modelo, fica a mensagem fixa e o registro das escritas.
            dlog('[runAgentLoop] fechamento no limite falhou:', e);
        }
        finally {
            typingFinal.remove();
        }
        setStatus('');
        if (!resumo)
            recordInterruptedTurn('max_steps');
        finalResponseText =
            resumo ||
                'Essa tarefa ficou longa demais para um único pedido e parei aqui. O que já fiz está no ' +
                    'documento — toque em **Continuar** para eu seguir de onde parei, ou divida o pedido em ' +
                    'partes menores (ex.: uma seção por vez).';
        const limitEl = addMessage('assistant', finalResponseText);
        if (didWrite && preWriteSnapshot) {
            undoSnapshot = preWriteSnapshot;
            attachUndo(limitEl);
        }
        attachContinue(limitEl);
        logStandard({
            type: 'WARNING',
            event: 'turn.step_limit',
            turn_id: turnId,
            detail: { steps: stepCount, writes: writes.length, summarized: !!resumo },
        });
        endTurn('MAX_STEPS', documentType);
    }
    catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        console.error('[runAgentLoop] erro fatal:', e);
        addMessage('assistant', `Não consegui concluir: ${errorMsg}`);
        recordInterruptedTurn('error', errorMsg);
        setStatus('erro de conexão');
        logStandard({
            type: 'ERROR',
            event: 'agent_loop.fatal',
            turn_id: turnId,
            message: errorMsg.slice(0, 300),
        });
        endTurn('ERROR', documentType);
    }
    finally {
        // Fim do turno (qualquer desfecho): se o documento foi alterado, tira os
        // parágrafos vazios que sobraram e roda o Validador, nessa ordem — validar
        // antes da faxina reportaria desvios que a própria faxina já ia resolver.
        //
        // A faxina roda AQUI, e não como ferramenta do modelo, porque não exige
        // julgamento nenhum: as regras são do rules.json e o modelo só teria a
        // chance de esquecer de chamar (a mesma falha que produziu o problema) e de
        // gastar um passo do LIMIT_STEPS. Ainda dentro do turno, portanto dentro do
        // escopo do "Desfazer".
        endTurnPersistence();
        // Grava JÁ que o pedido terminou (sem documento, rápido). O salvamento
        // completo espera 2 s pela faxina, e fechar o Word nessa janela deixava no
        // banco a marca "em andamento" do início do turno: a retomada dizia que o
        // pedido não terminou — até de um turno que só fez uma pergunta.
        void persistConversation({ comDocumento: false });
        // Assinatura do documento no FIM do turno — baseline do drift-check do
        // próximo turno (detecta Ctrl+Z/edição manual entre turnos). Tirada DEPOIS
        // da faxina: antes, a faxina removia parágrafos vazios depois da leitura e
        // o turno seguinte acusava "documento mudou fora do chat" por causa dela.
        // Até lá, sem baseline — e sem baseline não há aviso (nem falso).
        docStateAtTurnEnd = null;
        const registrarBaseline = async () => {
            try {
                const endMeta = await getDocumentMetadata();
                docStateAtTurnEnd = { words: endMeta.word_count, paragraphs: endMeta.paragraph_count };
            }
            catch (_a) {
                docStateAtTurnEnd = null;
            }
        };
        if (didWrite) {
            // `try/finally` de sintaxe (não `Promise.finally`, que exigiria lib
            // es2018): a validação roda mesmo que a faxina falhe.
            void (async () => {
                try {
                    const limpeza = await normalizeSpacing();
                    if (limpeza.removed || limpeza.inserted) {
                        logStandard({
                            type: 'INFO',
                            event: 'cleanup.spacing',
                            turn_id: turnId,
                            detail: {
                                removed: limpeza.removed,
                                inserted: limpeza.inserted,
                                truncated: limpeza.truncated,
                            },
                        });
                    }
                }
                finally {
                    await registrarBaseline();
                    scheduleValidation();
                    // Depois da faxina: o documento salvo é o mesmo que o usuário vê.
                    scheduleAutosave();
                }
            })();
        }
        else {
            await registrarBaseline();
            // Turno que não escreveu (pergunta, esclarecimento) também vale salvar —
            // a conversa mudou, ainda que o documento não.
            scheduleAutosave();
        }
    }
}
/**
 * Lê contexto do documento (Office.js).
 */
async function getDocumentMetadata() {
    try {
        return await Word.run(async (context) => {
            var _a;
            const body = context.document.body;
            body.load('text');
            const paragraphs = body.paragraphs;
            paragraphs.load('text');
            const ccs = context.document.contentControls;
            ccs.load('tag,title,text');
            await context.sync();
            const wordCount = ((_a = body.text.match(/\S+/g)) !== null && _a !== void 0 ? _a : []).length;
            return {
                word_count: wordCount,
                paragraph_count: paragraphs.items.length,
                content_controls: ccs.items.map((c) => ({
                    tag: c.tag,
                    title: c.title,
                    text: c.text,
                })),
            };
        });
    }
    catch (error) {
        console.error('[getDocumentMetadata] erro ao ler documento:', error);
        // Retorna metadados vazios em caso de erro
        return {
            word_count: 0,
            paragraph_count: 0,
            content_controls: [],
        };
    }
}
/**
 * Lê seleção atual (Office.js).
 */
async function getSelection() {
    try {
        return await Word.run(async (context) => {
            const sel = context.document.getSelection();
            sel.load('text');
            sel.font.load('name,size');
            await context.sync();
            if (!sel.text.trim())
                return null;
            return {
                text: sel.text,
                font_name: sel.font.name || undefined,
                font_size: sel.font.size || undefined,
            };
        });
    }
    catch (error) {
        console.error('[getSelection] erro ao ler seleção:', error);
        return null;
    }
}
function greet(typeName, autoFormatting = false) {
    // No "Formatar" a primeira mensagem é o resumo do que a formatação fez
    // (announceAndReviewFormatting), não um convite.
    if (autoFormatting)
        return;
    addMessage('assistant', `${greetAsk(typeName)}\n\n${GREET_CAVEAT}`);
}
/** "a Proposta Técnica" / "o documento" (tipo Outro). Artigo vem do catálogo. */
function greetAsk(typeName) {
    const about = documentType === 'outro' || typeName === 'Outro'
        ? 'o documento'
        : `${articleForType(typeName)} ${typeName}`;
    return (`Contextualize-me sobre ${about} que você quer escrever, como o cliente, ` +
        'temática ou até mesmo enviar um anexo para servir de referência.');
}
function articleForType(typeName) {
    const hit = templateCatalog.find((t) => t.id === documentType || t.name === typeName);
    return (hit === null || hit === void 0 ? void 0 : hit.article) === 'o' ? 'o' : 'a';
}
function setStatus(text) {
    const statusEl = document.getElementById('status');
    if (statusEl)
        statusEl.textContent = statusPrefix && text ? `${statusPrefix} · ${text}` : text;
}
/** Rótulo humano do tier do modelo (agnóstico ao provedor/modelo). */
function tierLabel(tier) {
    switch (tier) {
        case 'simple': return 'Simples';
        case 'complex': return 'Complexo';
        case 'standard': return 'Padrão';
        default: return '';
    }
}
/** Resumo compacto do turno na linha de status (discreto). Ex.:
 *  "Padrão · 1.240 tokens · 67% do cache". Vazio se não houve uso. */
function formatTokenStatus(t, tier) {
    const total = t.input + t.output + t.cacheRead;
    if (total <= 0)
        return '';
    const fmt = (n) => n.toLocaleString('pt-BR');
    const inputTotal = t.input + t.cacheRead;
    const cachePct = inputTotal > 0 ? Math.round((t.cacheRead / inputTotal) * 100) : 0;
    // Compacto de propósito: a linha de status é mono 8,5px MAIÚSCULO e divide o
    // espaço com o fio que varre (flex: 1). Texto longo engole o fio.
    const cacheNote = cachePct > 0 ? ` · ${cachePct}% cache` : '';
    const label = tierLabel(tier);
    const tierNote = label ? `${label} · ` : '';
    return `${tierNote}${fmt(total)} tokens${cacheNote}`;
}
/**
 * Alterna o botão de envio entre os estados "enviar" e "parar" — mesmo botão,
 * mesma posição (padrão ChatGPT/Claude), em vez de um botão "Parar" à parte.
 * Fica clicável nos dois estados: parado, envia; processando, aborta.
 */
function setBusy(busy) {
    sendBtn.classList.toggle('busy', busy);
    sendBtn.title = busy ? 'Parar' : 'Enviar';
    sendBtn.setAttribute('aria-label', busy ? 'Parar' : 'Enviar');
    // Campo principal VISÍVEL, porém sem aceitar digitação enquanto o turno roda:
    // texto escrito no meio da execução só se perderia (o envio está bloqueado) e
    // dava a impressão de que a mensagem tinha entrado na fila. O botão continua
    // clicável de propósito — é ele que aborta.
    promptEl.disabled = busy;
    promptEl.placeholder = busy ? 'Trabalhando… aguarde este turno terminar' : PROMPT_PLACEHOLDER;
    const attachBtn = document.getElementById('attach-btn');
    if (attachBtn)
        attachBtn.disabled = busy;
}
function addMessage(role, text) {
    const msg = document.createElement('div');
    msg.className = `msg ${role}`;
    // UI minimalista: sem rótulo de papel ("mIAu"/"Você") acima do balão —
    // o lado/estilo do balão já distingue quem falou.
    const bubble = document.createElement('div');
    bubble.className = 'bubble';
    if (role === 'assistant') {
        // Respostas do mIAu vêm em markdown -> renderiza como HTML sanitizado.
        bubble.classList.add('markdown');
        bubble.innerHTML = renderMarkdown(text);
    }
    else {
        // Mensagem do usuário: texto puro (mais seguro, sem renderizar markdown).
        bubble.textContent = text;
    }
    // A bolha entra na transcrição salva. O `messages` do modelo não serve para
    // redesenhar a tela (tem tool_use, tool_result e blocos de contexto), então o
    // painel guarda o que ele mesmo desenhou — ver util/session.ts.
    msg.dataset.transcriptIndex = String(transcript.length);
    transcript.push({ role, text });
    msg.append(bubble);
    messagesEl.appendChild(msg);
    scrollToEnd();
    return msg;
}
function replaceAssistantMessage(host, text) {
    if (!host) {
        addMessage('assistant', text);
        return;
    }
    let bubble = host.querySelector('.bubble');
    if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'bubble markdown';
        host.prepend(bubble);
    }
    bubble.classList.add('markdown');
    bubble.innerHTML = renderMarkdown(text);
    // Mantém a transcrição igual ao que está na tela (ex.: a bolha vira
    // "Alterações desfeitas." depois do Desfazer).
    const idx = Number(host.dataset.transcriptIndex);
    if (Number.isInteger(idx) && transcript[idx])
        transcript[idx] = { role: 'assistant', text };
    scrollToEnd();
}
function showTyping() {
    const msg = document.createElement('div');
    msg.className = 'msg assistant typing';
    msg.innerHTML =
        '<div class="bubble"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>';
    messagesEl.appendChild(msg);
    scrollToEnd();
    return msg;
}
function autoGrow() {
    promptEl.style.height = 'auto';
    // Campo vazio volta para a altura de uma linha (a do rows=1, via CSS): o
    // placeholder entra no scrollHeight e faria a caixa crescer sem texto algum.
    if (!promptEl.value) {
        promptEl.style.height = '';
        return;
    }
    promptEl.style.height = `${Math.min(promptEl.scrollHeight, 140)}px`;
}
function scrollToEnd() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
}
/**
 * Ancora um botão "Desfazer" ABAIXO da resposta do mIAu que alterou o
 * documento (em `host`). Remove qualquer botão anterior — só a ÚLTIMA
 * alteração é desfazível. Se `host` for nulo (turno sem texto final), cria um
 * container mínimo só para o botão.
 */
function attachUndo(host) {
    removeUndo();
    const btn = document.createElement('button');
    btn.className = 'undo-btn';
    btn.type = 'button';
    btn.textContent = 'Desfazer';
    btn.onclick = () => undoLastChanges();
    // O Desfazer vem primeiro na fileira: é o que desfaz, o resto propõe.
    turnActionsOf(host).prepend(btn);
    currentUndoEl = btn;
    scrollToEnd();
}
/**
 * Fileira de ações abaixo de uma resposta (Desfazer, Continuar, Refazer). Um
 * contêiner só por resposta, para as pílulas ficarem lado a lado em vez de uma
 * embaixo da outra. Sem `host` (turno sem texto final), cria uma mensagem
 * mínima só para as ações.
 */
function turnActionsOf(host) {
    let target = host;
    if (!target) {
        target = document.createElement('div');
        target.className = 'msg assistant';
        messagesEl.appendChild(target);
    }
    let row = target.querySelector(':scope > .turn-actions');
    if (!row) {
        row = document.createElement('div');
        row.className = 'turn-actions';
        target.appendChild(row);
    }
    return row;
}
/** Tira a fileira quando ela fica vazia (não deixa margem órfã na tela). */
function dropEmptyTurnActions(btn) {
    const row = btn.parentElement;
    btn.remove();
    if ((row === null || row === void 0 ? void 0 : row.classList.contains('turn-actions')) && !row.children.length)
        row.remove();
}
/** Remove o botão "Desfazer" atual (a alteração passa a ser "aceita"). */
function removeUndo() {
    if (currentUndoEl) {
        dropEmptyTurnActions(currentUndoEl);
        currentUndoEl = null;
    }
}
/** Botão "Continuar" do turno que parou no limite de passos. Só um por vez. */
let currentContinueEl = null;
/**
 * Pílula que manda um pedido pronto — "Continuar" depois do limite de passos,
 * "Refazer o pedido" depois de um turno interrompido. O pedido vai como se o
 * usuário o tivesse digitado: aparece no chat e segue o caminho normal.
 */
function turnActionButton(label, prompt) {
    const btn = document.createElement('button');
    btn.className = 'turn-action-btn';
    btn.type = 'button';
    btn.textContent = label;
    btn.onclick = () => {
        if (sendBtn.classList.contains('busy') || pendingAsk)
            return;
        dropEmptyTurnActions(btn);
        if (currentContinueEl === btn)
            currentContinueEl = null;
        promptEl.value = prompt;
        void send();
    };
    return btn;
}
function attachContinue(host) {
    removeContinue();
    const btn = turnActionButton('Continuar', 'Continue de onde parou.');
    turnActionsOf(host).appendChild(btn);
    currentContinueEl = btn;
    scrollToEnd();
}
function removeContinue() {
    if (currentContinueEl) {
        dropEmptyTurnActions(currentContinueEl);
        currentContinueEl = null;
    }
}
/**
 * Nota discreta sob a mensagem do usuário (ex.: "considerei as alterações
 * feitas direto no documento"). Só na tela: não vira bolha, não entra na
 * transcrição salva.
 */
function addTurnNote(anchorEl, text) {
    const note = document.createElement('div');
    note.className = 'turn-note';
    note.textContent = text;
    anchorEl.appendChild(note);
}
/**
 * Desfaz as alterações da ÚLTIMA resposta do mIAu, restaurando o snapshot
 * (OOXML) tirado antes delas. Reverte só o corpo do documento; capa,
 * cabeçalho e rodapé (partes de seção) não são afetados.
 */
async function undoLastChanges() {
    if (!undoSnapshot)
        return;
    const undoHost = currentUndoEl === null || currentUndoEl === void 0 ? void 0 : currentUndoEl.closest('.msg');
    removeUndo();
    setStatus('desfazendo');
    try {
        // Tracking SUSPENSO durante a restauração: com controle de alterações
        // ligado, o insertOoxml(replace) marcaria o corpo INTEIRO como apagado +
        // reinserido — uma revisão gigante e ilegível, em vez de um estado limpo.
        // Desfazer é "voltar ao estado anterior", não uma edição a revisar.
        await withTrackChangesSuspended(async () => {
            await Word.run(async (context) => {
                context.document.body.insertOoxml(undoSnapshot, Word.InsertLocation.replace);
                await context.sync();
            });
        });
        undoSnapshot = null;
        replaceAssistantMessage(undoHost, 'Alterações desfeitas.');
        setStatus('');
        // O modelo precisa saber que o documento REVERTEU: sem esta nota, o
        // histórico o faz assumir que as edições do turno anterior ainda existem.
        documentRevertedNote = true;
        scheduleValidation();
        // E o banco também: sem salvar aqui, a conversa guardada continuava com o
        // documento de ANTES do Desfazer — reabrir pelo histórico trazia de volta
        // exatamente o que o usuário tinha desfeito.
        scheduleAutosave();
        logStandard({ type: 'INFO', event: 'undo.applied', component: 'office_tool' });
    }
    catch (e) {
        console.error('[undoLastChanges] falhou:', e);
        undoSnapshot = null;
        setStatus('');
        addMessage('assistant', 'Não consegui desfazer automaticamente. Use **Ctrl+Z** no Word para reverter.');
        logStandard({
            type: 'ERROR',
            event: 'undo.failed',
            component: 'office_tool',
            message: redactToolError(String(e)),
        });
    }
}
// Inicia o add-in quando o documento carrega
dlog('[startup] mIAu iniciando...');
initializeAddin();
